// Gestion para obtener configuracion de envio desde Shopify
class GestorConfiguracionEnvio {
  static async obtenerCostoEnvioDomicilio() {
    // Valor por defecto que ya esta configurado en window
    const costoDefault = window.costoEnvioDomicilio != null ? Number(window.costoEnvioDomicilio) : 10;

    // Validar configuracion
    if (!window.urlConsulta || !window.backendShopify) {
      console.warn("GestorConfiguracionEnvio - Configuracion faltante, usando valor por defecto:", costoDefault);
      return costoDefault;
    }

    const query = `{
      deliveryProfiles(first: 10) {
        edges {
          node {
            id
            name
            profileLocationGroups {
              locationGroupZones(first: 20) {
                edges {
                  node {
                    zone {
                      id
                      name
                    }
                    methodDefinitions(first: 10) {
                      edges {
                        node {
                          id
                          name
                          active
                          rateProvider {
                            ... on DeliveryRateDefinition {
                              price {
                                amount
                                currencyCode
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }`;

    try {
      //console.log("GestorConfiguracionEnvio - Consultando costo de envio con query:", JSON.stringify({ query }),);
      const respuesta = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      });

      if (!respuesta.ok) {
        console.warn("GestorConfiguracionEnvio - Error de red, usando valor por defecto:", costoDefault);
        return costoDefault;
      }

      const datos = await respuesta.json();

      // Navegar por la estructura de datos para obtener el precio
      const profiles = datos?.data?.deliveryProfiles?.edges;
      if (!profiles || profiles.length == 0) {
        console.warn("GestorConfiguracionEnvio - No hay perfiles de envio, usando valor por defecto:", costoDefault);
        return costoDefault;
      }

      // Buscar el primer methodDefinition activo con precio
      for (const profile of profiles) {
        const locationGroups = profile?.node?.profileLocationGroups;
        if (!locationGroups) continue;

        for (const group of locationGroups) {
          const zones = group?.locationGroupZones?.edges;
          if (!zones) continue;

          for (const zone of zones) {
            const methods = zone?.node?.methodDefinitions?.edges;
            if (!methods) continue;

            for (const method of methods) {
              const methodNode = method?.node;
              if (methodNode?.active && methodNode?.rateProvider?.price?.amount != null) {
                const costo = parseFloat(methodNode.rateProvider.price.amount);
                if (costo >= 0) return costo;
              }
            }
          }
        }
      }

      console.warn("GestorConfiguracionEnvio - No se encontro precio en la respuesta, usando valor por defecto:", costoDefault);
      return costoDefault;

    } catch (error) {
      console.error("GestorConfiguracionEnvio - Error al obtener costo de envio:", error.message);
      console.warn("GestorConfiguracionEnvio - Usando valor por defecto:", costoDefault);
      return costoDefault;
    }
  }

  static async actualizarCostoEnvioGlobal() {
    try {
      const costo = await this.obtenerCostoEnvioDomicilio();
      window.costoEnvioDomicilio = costo;
      return costo;
    } catch (error) {
      console.error("GestorConfiguracionEnvio - Error al actualizar costo global:", error.message);
      return window.costoEnvioDomicilio != null ? Number(window.costoEnvioDomicilio) : 10;
    }
  }
}

// Gestion para los empleados de Call Center (Nexocorp)
class GestorEmpleadoCCShopify {
  static async validarCorreoElectronicoParaCC(correoElectronico) {
    // Validaciones iniciales
    if (!correoElectronico || typeof correoElectronico !== "string") {
      const errorDetail = `Validación correo - Email inválido: ${correoElectronico} `;
      console.error(errorDetail);
      throw new Error("El correo electrónico es requerido y debe ser válido.");
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(correoElectronico)) {
      const errorDetail = `Validación correo - Formato email inválido: ${correoElectronico} `;
      console.error(errorDetail);
      throw new Error("El formato del correo electrónico no es válido.");
    }

    // Validar configuración
    if (!window.urlConsulta) {
      const errorDetail = `Validación correo - Configuración faltante: URL = ${window.urlConsulta}`;
      console.error(errorDetail);
      throw new Error(
        "Error de configuración del sistema. Por favor, contacta al soporte técnico."
      );
    }

    const graphQLQuery = `
      query($identifier: CustomerIdentifierInput!) {
        customer: customerByIdentifier(identifier: $identifier) {
          id
          firstName
          lastName
          email
          phone
          metafield(namespace: "informacion", key: "extra") {
            id
            key
            namespace
            value
          }
        }
      }
    `;

    const variables = {
      identifier: {
        emailAddress: correoElectronico,
      },
    };

    try {
      const respuesta = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: graphQLQuery,
          variables: variables,
        }),
      });

      if (!respuesta.ok) {
        const errorDetail = `Validación correo - Error de red: ${respuesta.status} ${respuesta.statusText} | URL: ${window.urlConsulta} | Email: ${correoElectronico} `;
        console.error(errorDetail);

        // Manejo específico por código de estado
        switch (respuesta.status) {
          case 401:
            throw new Error(
              "Sesión expirada. Por favor, recarga la página e intenta nuevamente."
            );
          case 403:
            throw new Error("No tienes permisos para realizar esta operación.");
          case 429:
            throw new Error(
              "Demasiadas solicitudes. Por favor, espera un momento e intenta nuevamente."
            );
          case 500:
          case 502:
          case 503:
          case 504:
            throw new Error(
              "El servidor está temporalmente no disponible. Por favor, intenta más tarde."
            );
          default:
            throw new Error(
              "Error de conexión con el servidor. Por favor, intenta nuevamente."
            );
        }
      }

      let datos;
      try {
        datos = await respuesta.json();
      } catch (parseError) {
        const errorDetail = `Validación correo - Error parseando respuesta: ${parseError.message} | Email: ${correoElectronico} `;
        console.error(errorDetail);
        throw new Error(
          "Error al procesar la respuesta del servidor. Por favor, intenta nuevamente."
        );
      }

      // Verificar errores de GraphQL
      if (datos.errors) {
        const errorDetail = `Validación correo - Errores GraphQL: ${JSON.stringify(
          datos.errors
        )} | Email: ${correoElectronico} `;
        console.error(errorDetail);

        // Buscar errores específicos
        const hasTimeoutError = datos.errors.some(
          (error) =>
            error.message?.toLowerCase().includes("timeout") ||
            error.message?.toLowerCase().includes("deadline")
        );

        if (hasTimeoutError) {
          throw new Error(
            "La operación tardó demasiado tiempo. Por favor, intenta nuevamente."
          );
        }

        throw new Error(
          "Error al consultar los datos. Por favor, intenta nuevamente."
        );
      }

      // Verificar estructura de respuesta
      if (!datos.data) {
        const errorDetail = `Validación correo - Respuesta sin data: ${JSON.stringify(
          datos
        )} | Email: ${correoElectronico} `;
        console.error(errorDetail);
        throw new Error(
          "Respuesta del servidor inválida. Por favor, intenta nuevamente."
        );
      }

      // Si no se encontró cliente, retornar null (no es error)
      if (!datos.data.customer) {
        console.log(
          `Validación correo - Cliente no encontrado: ${correoElectronico} `
        );
        return null;
      }

      const customer = datos.data.customer;

      // Validar datos del cliente
      if (!customer.id) {
        const errorDetail = `Validación correo - Cliente sin ID: ${JSON.stringify(
          customer
        )} | Email: ${correoElectronico} `;
        console.error(errorDetail);
        throw new Error(
          "Datos del cliente incompletos. Por favor, contacta al soporte técnico."
        );
      }

      // Procesar metafields
      let metafieldData = {};
      if (customer?.metafield?.value) {
        try {
          metafieldData = JSON.parse(customer.metafield.value);
        } catch (parseError) {
          const errorDetail = `Validación correo - Error parseando metafield: ${parseError.message} | Email: ${correoElectronico} | Metafield: ${customer.metafield.value} `;
          console.warn(errorDetail);
          // No lanzar error, usar objeto vacío
          metafieldData = {};
        }
      }

      console.log(
        `Validación correo - Cliente encontrado exitosamente: ${correoElectronico} | ID: ${customer.id} `
      );

      return {
        id: customer.id,
        nombre: customer.firstName || "",
        apellido: customer.lastName || "",
        email: customer.email || "",
        celular: customer.phone?.replace(/^\+591/, "") || "",
        ci: metafieldData.ci || "",
        direcciones: metafieldData.direcciones || [],
        razon_social: metafieldData.razon_social || "",
        nit: metafieldData.nit || "",
        fecha_nacimiento: metafieldData.fecha || "",
        permisosHutCoins: metafieldData.permisosHutCoins || false,
      };
    } catch (error) {
      // Si es un error que ya formateamos, relanzarlo
      if (
        error.message.includes("correo electrónico") ||
        error.message.includes("Sesión expirada") ||
        error.message.includes("No tienes permisos") ||
        error.message.includes("Demasiadas solicitudes") ||
        error.message.includes("servidor está temporalmente") ||
        error.message.includes("Error de conexión") ||
        error.message.includes("Error al procesar") ||
        error.message.includes("Error al consultar") ||
        error.message.includes("La operación tardó") ||
        error.message.includes("Respuesta del servidor") ||
        error.message.includes("Datos del cliente") ||
        error.message.includes("Error de configuración")
      ) {
        throw error;
      }

      // Errores de red específicos
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        const errorDetail = `Validación correo - Error de red fetch: ${error.message} | Email: ${correoElectronico} `;
        console.error(errorDetail);
        throw new Error(
          "Error de conexión. Verifica tu conexión a internet e intenta nuevamente."
        );
      }

      if (error.name === "AbortError") {
        const errorDetail = `Validación correo - Timeout: ${error.message} | Email: ${correoElectronico} `;
        console.error(errorDetail);
        throw new Error(
          "La operación tardó demasiado tiempo. Por favor, intenta nuevamente."
        );
      }

      // Error no previsto
      const errorDetail = `Validación correo - Error inesperado: ${error.message} | Email: ${correoElectronico} | Stack: ${error.stack} `;
      console.error(errorDetail);
      throw new Error(
        "Ha ocurrido un error inesperado. Por favor, contacta al soporte técnico."
      );
    }
  }

  static async validarNumeroTelefonoParaCC(numeroTelefono) {
    // Validaciones iniciales
    if (!numeroTelefono || typeof numeroTelefono !== "string") {
      const errorDetail = `Validación teléfono - Teléfono inválido: ${numeroTelefono} `;
      console.error(errorDetail);
      throw new Error("El número de teléfono es requerido y debe ser válido.");
    }

    // Validar formato básico (solo números, puede tener espacios o guiones)
    const phonePattern = /^[\d\s\-+()]+$/;
    if (!phonePattern.test(numeroTelefono)) {
      const errorDetail = `Validación teléfono - Formato teléfono inválido: ${numeroTelefono} `;
      console.error(errorDetail);
      throw new Error("El formato del número de teléfono no es válido.");
    }

    // Validar configuración
    if (!window.urlConsulta) {
      const errorDetail = `Validación teléfono - Configuración faltante: URL = ${window.urlConsulta}`;
      console.error(errorDetail);
      throw new Error(
        "Error de configuración del sistema. Por favor, contacta al soporte técnico."
      );
    }

    const graphQLQuery = `
      query($identifier: CustomerIdentifierInput!) {
        customer: customerByIdentifier(identifier: $identifier) {
          id
          firstName
          lastName
          email
          phone
          metafield(namespace: "informacion", key: "extra") {
            id
            key
            namespace
            value
          }
        }
      }
    `;

    const variables = {
      identifier: {
        phoneNumber: numeroTelefono,
      },
    };

    try {
      const respuesta = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: graphQLQuery,
          variables: variables,
        }),
      });

      if (!respuesta.ok) {
        const errorDetail = `Validación teléfono - Error de red: ${respuesta.status} ${respuesta.statusText} | URL: ${window.urlConsulta} | Teléfono: ${numeroTelefono} `;
        console.error(errorDetail);

        // Manejo específico por código de estado
        switch (respuesta.status) {
          case 401:
            throw new Error(
              "Sesión expirada. Por favor, recarga la página e intenta nuevamente."
            );
          case 403:
            throw new Error("No tienes permisos para realizar esta operación.");
          case 429:
            throw new Error(
              "Demasiadas solicitudes. Por favor, espera un momento e intenta nuevamente."
            );
          case 500:
          case 502:
          case 503:
          case 504:
            throw new Error(
              "El servidor está temporalmente no disponible. Por favor, intenta más tarde."
            );
          default:
            throw new Error(
              "Error de conexión con el servidor. Por favor, intenta nuevamente."
            );
        }
      }

      let datos;
      try {
        datos = await respuesta.json();
      } catch (parseError) {
        const errorDetail = `Validación teléfono - Error parseando respuesta: ${parseError.message} | Teléfono: ${numeroTelefono} `;
        console.error(errorDetail);
        throw new Error(
          "Error al procesar la respuesta del servidor. Por favor, intenta nuevamente."
        );
      }

      // Verificar errores de GraphQL
      if (datos.errors) {
        const errorDetail = `Validación teléfono - Errores GraphQL: ${JSON.stringify(
          datos.errors
        )} | Teléfono: ${numeroTelefono} `;
        console.error(errorDetail);

        // Buscar errores específicos
        const hasTimeoutError = datos.errors.some(
          (error) =>
            error.message?.toLowerCase().includes("timeout") ||
            error.message?.toLowerCase().includes("deadline")
        );

        if (hasTimeoutError) {
          throw new Error(
            "La operación tardó demasiado tiempo. Por favor, intenta nuevamente."
          );
        }

        throw new Error(
          "Error al consultar los datos. Por favor, intenta nuevamente."
        );
      }

      // Verificar estructura de respuesta
      if (!datos.data) {
        const errorDetail = `Validación teléfono - Respuesta sin data: ${JSON.stringify(
          datos
        )} | Teléfono: ${numeroTelefono} `;
        console.error(errorDetail);
        throw new Error(
          "Respuesta del servidor inválida. Por favor, intenta nuevamente."
        );
      }

      // Si no se encontró cliente, retornar null (no es error)
      if (!datos.data.customer) {
        console.log(
          `Validación teléfono - Cliente no encontrado: ${numeroTelefono} `
        );
        return null;
      }

      const customer = datos.data.customer;

      // Validar datos del cliente
      if (!customer.id) {
        const errorDetail = `Validación teléfono - Cliente sin ID: ${JSON.stringify(
          customer
        )} | Teléfono: ${numeroTelefono} `;
        console.error(errorDetail);
        throw new Error(
          "Datos del cliente incompletos. Por favor, contacta al soporte técnico."
        );
      }

      // Procesar metafields
      let metafieldData = {};
      if (customer?.metafield?.value) {
        try {
          metafieldData = JSON.parse(customer.metafield.value);
        } catch (parseError) {
          const errorDetail = `Validación teléfono - Error parseando metafield: ${parseError.message} | Teléfono: ${numeroTelefono} | Metafield: ${customer.metafield.value} `;
          console.warn(errorDetail);
          metafieldData = {};
        }
      }

      console.log(
        `Validación teléfono - Cliente encontrado exitosamente: ${numeroTelefono} | ID: ${customer.id} `
      );

      return {
        id: customer.id,
        nombre: customer.firstName || "",
        apellido: customer.lastName || "",
        email: customer.email || "",
        celular: customer.phone?.replace(/^\+591/, "") || "",
        ci: metafieldData.ci || "",
        direcciones: metafieldData.direcciones || [],
        razon_social: metafieldData.razon_social || "",
        nit: metafieldData.nit || "",
        fecha_nacimiento: metafieldData.fecha || "",
        permisosHutCoins: metafieldData.permisosHutCoins || false,
      };
    } catch (error) {
      // Si es un error que ya formateamos, relanzarlo
      if (
        error.message.includes("número de teléfono") ||
        error.message.includes("Sesión expirada") ||
        error.message.includes("No tienes permisos") ||
        error.message.includes("Demasiadas solicitudes") ||
        error.message.includes("servidor está temporalmente") ||
        error.message.includes("Error de conexión") ||
        error.message.includes("Error al procesar") ||
        error.message.includes("Error al consultar") ||
        error.message.includes("La operación tardó") ||
        error.message.includes("Respuesta del servidor") ||
        error.message.includes("Datos del cliente") ||
        error.message.includes("Error de configuración")
      ) {
        throw error;
      }

      // Errores de red específicos
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        const errorDetail = `Validación teléfono - Error de red fetch: ${error.message} | Teléfono: ${numeroTelefono} `;
        console.error(errorDetail);
        throw new Error(
          "Error de conexión. Verifica tu conexión a internet e intenta nuevamente."
        );
      }

      if (error.name === "AbortError") {
        const errorDetail = `Validación teléfono - Timeout: ${error.message} | Teléfono: ${numeroTelefono} `;
        console.error(errorDetail);
        throw new Error(
          "La operación tardó demasiado tiempo. Por favor, intenta nuevamente."
        );
      }

      // Error no previsto
      const errorDetail = `Validación teléfono - Error inesperado: ${error.message} | Teléfono: ${numeroTelefono} | Stack: ${error.stack} `;
      console.error(errorDetail);
      throw new Error(
        "Ha ocurrido un error inesperado. Por favor, contacta al soporte técnico."
      );
    }
  }

  static async actualizaClienteParaCC(datosCliente) {
    // Validar que se recibió el objeto con los datos del cliente
    if (!datosCliente || !datosCliente.id) {
      console.log("ERROR ACTUALIZACION CLIENTE - Datos inválidos:", {
        timestamp: new Date().toISOString(),
        error: "Datos de cliente faltantes o inválidos",
        datosRecibidos: datosCliente,
        stackTrace: new Error().stack,
      });
      throw new Error(
        "No se pudieron validar tus datos. Por favor, revisa la información ingresada"
      );
    }

    // Validar configuración
    if (!window.urlConsulta) {
      console.log("ERROR ACTUALIZACION CLIENTE - Configuración faltante:", {
        timestamp: new Date().toISOString(),
        error: "Configuración del sistema incompleta",
        urlConsulta: !!window.urlConsulta,
        urlBackend: !!window.urlConsulta,
        stackTrace: new Error().stack,
      });
      throw new Error(
        "Error de configuración del sistema. Por favor, contacta al soporte técnico."
      );
    }

    const graphQLQuery = `
  mutation UpdateCustomer($input: CustomerInput!) {
  customerUpdate(input: $input) {
      customer {
      id
      firstName
      lastName
      email
      phone
      metafield(namespace: "informacion", key: "extra") {
        id
        key
        namespace
        value
      }
    }
      userErrors {
      field
      message
    }
  }
}
`;

    // Preparar la información extra para el metafield
    const informacionExtra = JSON.stringify({
      nit: datosCliente.nit || "",
      razon_social: datosCliente.razon_social || "",
      fecha: datosCliente.fecha_nacimiento || "",
      permisosHutCoins: datosCliente.permisosHutCoins || false,
      ci: datosCliente.ci || "",
      direcciones: datosCliente.direcciones || [],
    });

    const variables = {
      input: {
        id:
          datosCliente.id && datosCliente.id.includes("gid://shopify/Customer/")
            ? datosCliente.id
            : `gid://shopify/Customer/${datosCliente.id}`,
        firstName: datosCliente.nombre || "",
        lastName: datosCliente.apellido || "",
        email: datosCliente.email || "",
        phone: datosCliente.celular ? `+591${datosCliente.celular}` : "",
        metafields: [
          {
            namespace: "informacion",
            key: "extra",
            type: "json_string",
            value: informacionExtra,
          },
        ],
      },
    };

    try {
      const respuesta = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: graphQLQuery,
          variables: variables,
        }),
      });

      if (!respuesta.ok) {
        console.log("ERROR ACTUALIZACION CLIENTE - Error de red:", {
          timestamp: new Date().toISOString(),
          error: "Error de respuesta HTTP",
          status: respuesta.status,
          statusText: respuesta.statusText,
          url: window.urlConsulta,
          clienteId: datosCliente.id,
          headers: Object.fromEntries(respuesta.headers),
          stackTrace: new Error().stack,
        });

        // Manejo específico por código de estado
        switch (respuesta.status) {
          case 401:
            throw new Error(
              "Sesión expirada. Por favor, recarga la página e intenta nuevamente."
            );
          case 403:
            throw new Error("No tienes permisos para realizar esta operación.");
          case 429:
            throw new Error(
              "Demasiadas solicitudes. Por favor, espera un momento e intenta nuevamente."
            );
          case 500:
          case 502:
          case 503:
          case 504:
            throw new Error(
              "El servidor está temporalmente no disponible. Por favor, intenta más tarde."
            );
          default:
            throw new Error(
              "Error de conexión con el servidor. Por favor, intenta nuevamente."
            );
        }
      }

      let datos;
      try {
        datos = await respuesta.json();
      } catch (parseError) {
        console.log(
          "ERROR ACTUALIZACION CLIENTE - Error parseando respuesta:",
          {
            timestamp: new Date().toISOString(),
            error: "Error al parsear respuesta JSON",
            parseError: parseError.message,
            clienteId: datosCliente.id,
            responseStatus: respuesta.status,
            stackTrace: parseError.stack,
          }
        );
        throw new Error(
          "Error al procesar la respuesta del servidor. Por favor, intenta nuevamente."
        );
      }

      console.log("Respuesta de actualización:", datos);

      // Verificar errores de GraphQL
      if (datos.errors) {
        console.log("ERROR ACTUALIZACION CLIENTE - Errores GraphQL:", {
          timestamp: new Date().toISOString(),
          error: "Errores en consulta GraphQL",
          graphQLErrors: datos.errors,
          clienteId: datosCliente.id,
          query: graphQLQuery,
          variables: variables,
          stackTrace: new Error().stack,
        });
        throw new Error(
          "Error al consultar los datos. Por favor, intenta nuevamente."
        );
      }

      // Verificar si hay errores en la respuesta de Shopify
      if (
        datos.data?.customerUpdate?.userErrors &&
        datos.data.customerUpdate.userErrors.length > 0
      ) {
        const userErrors = datos.data.customerUpdate.userErrors;

        console.log("ERROR ACTUALIZACION CLIENTE - Errores de usuario:", {
          timestamp: new Date().toISOString(),
          error: "Errores de validación de Shopify",
          userErrors: userErrors,
          clienteId: datosCliente.id,
          datosEnviados: variables.input,
          stackTrace: new Error().stack,
        });

        // Buscar específicamente error de email duplicado
        const emailError = userErrors.find(
          (error) =>
            error.field?.includes("email") &&
            error.message?.includes("already exists")
        );

        if (emailError) {
          throw new Error(
            "El correo electrónico ya está registrado. Por favor, intenta con otro correo."
          );
        }

        // Para otros tipos de errores
        throw new Error("Revisa tus datos, por favor.");
      }

      // Verificar si el customer es null
      if (datos.data?.customerUpdate?.customer === null) {
        console.log("ERROR ACTUALIZACION CLIENTE - Cliente nulo:", {
          timestamp: new Date().toISOString(),
          error: "La actualización devolvió cliente nulo",
          clienteId: datosCliente.id,
          respuestaCompleta: datos,
          stackTrace: new Error().stack,
        });
        throw new Error(
          "No se pudo actualizar la información. Verifica tus datos."
        );
      }

      // Obtener el cliente actualizado
      const customer = datos.data?.customerUpdate?.customer;
      if (!customer) {
        console.log(
          "ERROR ACTUALIZACION CLIENTE - Cliente no encontrado en respuesta:",
          {
            timestamp: new Date().toISOString(),
            error: "Cliente no encontrado en respuesta exitosa",
            clienteId: datosCliente.id,
            respuestaCompleta: datos,
            stackTrace: new Error().stack,
          }
        );
        throw new Error(
          "No se pudo obtener la información actualizada del cliente."
        );
      }

      // Procesar los metafields para extraer la información extra
      let metafieldData = {};
      if (customer?.metafield?.value) {
        try {
          metafieldData = JSON.parse(customer.metafield.value);
        } catch (e) {
          console.log(
            "ERROR ACTUALIZACION CLIENTE - Error parseando metafield:",
            {
              timestamp: new Date().toISOString(),
              error: "Error al parsear metafield",
              parseError: e.message,
              clienteId: customer.id,
              metafieldValue: customer.metafield.value,
              stackTrace: e.stack,
            }
          );
          metafieldData = {};
        }
      }

      // Devolver el objeto actualizado
      return {
        id: customer.id,
        nombre: customer.firstName || "",
        apellido: customer.lastName || "",
        email: customer.email || "",
        celular: customer.phone?.replace(/^\+591/, "") || "",
        ci: metafieldData.ci || "",
        direcciones: metafieldData.direcciones || [],
        razon_social: metafieldData.razon_social || "",
        nit: metafieldData.nit || "",
        fecha_nacimiento: metafieldData.fecha || "",
        permisosHutCoins: metafieldData.permisosHutCoins || false,
      };
    } catch (error) {
      // Si es un error que ya formateamos, relanzarlo
      if (
        error.message.includes("No se pudieron validar") ||
        error.message.includes("Error de configuración") ||
        error.message.includes("Sesión expirada") ||
        error.message.includes("No tienes permisos") ||
        error.message.includes("Demasiadas solicitudes") ||
        error.message.includes("servidor está temporalmente") ||
        error.message.includes("Error de conexión") ||
        error.message.includes("Error al procesar") ||
        error.message.includes("Error al consultar") ||
        error.message.includes("correo electrónico ya está") ||
        error.message.includes("Revisa tus datos") ||
        error.message.includes("No se pudo actualizar") ||
        error.message.includes("No se pudo obtener")
      ) {
        throw error;
      }

      console.log("ERROR ACTUALIZACION CLIENTE - Error inesperado:", {
        timestamp: new Date().toISOString(),
        error: "Error no controlado en actualización",
        errorName: error.name,
        errorMessage: error.message,
        clienteId: datosCliente?.id,
        stackTrace: error.stack,
        variables: variables,
      });

      // Manejar errores de red específicos
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          "Error de conexión. Verifica tu conexión a internet e intenta nuevamente."
        );
      }

      if (error.name === "AbortError") {
        throw new Error(
          "La operación tardó demasiado tiempo. Por favor, intenta nuevamente."
        );
      }

      throw new Error(
        "Ha ocurrido un error al actualizar los datos. Inténtalo de nuevo."
      );
    }
  }

  static async crearClienteParaCC(datosCliente) {
    // Validar que se recibieron los datos mínimos
    if (!datosCliente || !datosCliente.email) {
      console.log("ERROR CREACION CLIENTE - Email faltante:", {
        timestamp: new Date().toISOString(),
        error: "Email requerido para crear cliente",
        datosRecibidos: datosCliente,
        stackTrace: new Error().stack,
      });
      throw new Error("Email es requerido para crear el cliente");
    }

    if (!datosCliente.celular) {
      console.log("ERROR CREACION CLIENTE - Celular faltante:", {
        timestamp: new Date().toISOString(),
        error: "Celular requerido para crear cliente",
        datosRecibidos: datosCliente,
        stackTrace: new Error().stack,
      });
      throw new Error("Número de celular es requerido");
    }

    // Validar configuración
    if (!window.urlConsulta) {
      console.log("ERROR CREACION CLIENTE - Configuración faltante:", {
        timestamp: new Date().toISOString(),
        error: "Configuración del sistema incompleta",
        urlConsulta: !!window.urlConsulta,
        urlBackend: !!window.urlConsulta,
        stackTrace: new Error().stack,
      });
      throw new Error(
        "Error de configuración del sistema. Por favor, contacta al soporte técnico."
      );
    }

    const graphQLQuery = `
  mutation CreateCustomer($input: CustomerInput!) {
    customerCreate(input: $input) {
      customer {
        id
        firstName
        lastName
        email
        phone
        metafield(namespace: "informacion", key: "extra") {
          id
          key
          namespace
          value
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

    // Preparar la información extra para el metafield
    const informacionExtra = JSON.stringify({
      nit: datosCliente.nit || "",
      razon_social: datosCliente.razon_social || "",
      fecha: datosCliente.fecha_nacimiento || "",
      permisosHutCoins: datosCliente.permisosHutCoins || false,
      ci: datosCliente.ci || "",
      direcciones: datosCliente.direcciones || [],
    });

    const variables = {
      input: {
        firstName: datosCliente.nombre || "",
        lastName: datosCliente.apellido || "",
        email: datosCliente.email,
        phone: `+591${datosCliente.celular}`,
        metafields: [
          {
            namespace: "informacion",
            key: "extra",
            type: "json_string",
            value: informacionExtra,
          },
        ],
      },
    };

    try {
      const respuesta = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: graphQLQuery,
          variables: variables,
        }),
      });

      if (!respuesta.ok) {
        console.log("ERROR CREACION CLIENTE - Error de red:", {
          timestamp: new Date().toISOString(),
          error: "Error de respuesta HTTP",
          status: respuesta.status,
          statusText: respuesta.statusText,
          url: window.urlConsulta,
          datosCliente: datosCliente,
          headers: Object.fromEntries(respuesta.headers),
          stackTrace: new Error().stack,
        });

        // Manejo específico por código de estado
        switch (respuesta.status) {
          case 401:
            throw new Error(
              "Sesión expirada. Por favor, recarga la página e intenta nuevamente."
            );
          case 403:
            throw new Error("No tienes permisos para realizar esta operación.");
          case 429:
            throw new Error(
              "Demasiadas solicitudes. Por favor, espera un momento e intenta nuevamente."
            );
          case 500:
          case 502:
          case 503:
          case 504:
            throw new Error(
              "El servidor está temporalmente no disponible. Por favor, intenta más tarde."
            );
          default:
            throw new Error(
              "Error de conexión con el servidor. Por favor, intenta nuevamente."
            );
        }
      }

      let datos;
      try {
        datos = await respuesta.json();
      } catch (parseError) {
        console.log("ERROR CREACION CLIENTE - Error parseando respuesta:", {
          timestamp: new Date().toISOString(),
          error: "Error al parsear respuesta JSON",
          parseError: parseError.message,
          datosCliente: datosCliente,
          responseStatus: respuesta.status,
          stackTrace: parseError.stack,
        });
        throw new Error(
          "Error al procesar la respuesta del servidor. Por favor, intenta nuevamente."
        );
      }

      console.log("Respuesta de creación:", datos);

      // Verificar errores de GraphQL
      if (datos.errors) {
        console.log("ERROR CREACION CLIENTE - Errores GraphQL:", {
          timestamp: new Date().toISOString(),
          error: "Errores en consulta GraphQL",
          graphQLErrors: datos.errors,
          datosCliente: datosCliente,
          query: graphQLQuery,
          variables: variables,
          stackTrace: new Error().stack,
        });
        throw new Error(
          "Error al consultar los datos. Por favor, intenta nuevamente."
        );
      }

      // Verificar si hay errores en la respuesta de Shopify
      if (
        datos.data?.customerCreate?.userErrors &&
        datos.data.customerCreate.userErrors.length > 0
      ) {
        const userErrors = datos.data.customerCreate.userErrors;

        console.log("ERROR CREACION CLIENTE - Errores de usuario:", {
          timestamp: new Date().toISOString(),
          error: "Errores de validación de Shopify",
          userErrors: userErrors,
          datosCliente: datosCliente,
          datosEnviados: variables.input,
          stackTrace: new Error().stack,
        });

        // Buscar específicamente error de email duplicado
        const emailError = userErrors.find(
          (error) =>
            error.field?.includes("email") &&
            error.message?.includes("already exists")
        );

        if (emailError) {
          throw new Error(
            "El correo electrónico ya está registrado. Por favor, intenta con otro correo."
          );
        }

        // Buscar error de teléfono duplicado
        const phoneError = userErrors.find(
          (error) =>
            error.field?.includes("phone") &&
            error.message?.includes("already exists")
        );

        if (phoneError) {
          throw new Error(
            "El número de teléfono ya está registrado. Por favor, intenta con otro número."
          );
        }

        // Para otros tipos de errores
        throw new Error("Revisa tus datos, por favor.");
      }

      // Verificar si el customer es null
      if (datos.data?.customerCreate?.customer === null) {
        console.log("ERROR CREACION CLIENTE - Cliente nulo:", {
          timestamp: new Date().toISOString(),
          error: "La creación devolvió cliente nulo",
          datosCliente: datosCliente,
          respuestaCompleta: datos,
          stackTrace: new Error().stack,
        });
        throw new Error("No se pudo crear el cliente. Verifica tus datos.");
      }

      // Obtener el cliente creado
      const customer = datos.data?.customerCreate?.customer;
      if (!customer) {
        console.log(
          "ERROR CREACION CLIENTE - Cliente no encontrado en respuesta:",
          {
            timestamp: new Date().toISOString(),
            error: "Cliente no encontrado en respuesta exitosa",
            datosCliente: datosCliente,
            respuestaCompleta: datos,
            stackTrace: new Error().stack,
          }
        );
        throw new Error(
          "No se pudo obtener la información del cliente creado."
        );
      }

      // Procesar los metafields para extraer la información extra
      let metafieldData = {};
      if (customer?.metafield?.value) {
        try {
          metafieldData = JSON.parse(customer.metafield.value);
        } catch (e) {
          console.log("ERROR CREACION CLIENTE - Error parseando metafield:", {
            timestamp: new Date().toISOString(),
            error: "Error al parsear metafield",
            parseError: e.message,
            clienteId: customer.id,
            metafieldValue: customer.metafield.value,
            stackTrace: e.stack,
          });
          metafieldData = {};
        }
      }

      // Devolver el objeto creado
      return {
        id: customer.id,
        nombre: customer.firstName || "",
        apellido: customer.lastName || "",
        email: customer.email || "",
        celular: customer.phone?.replace(/^\+591/, "") || "",
        ci: metafieldData.ci || "",
        direcciones: metafieldData.direcciones || [],
        razon_social: metafieldData.razon_social || "",
        nit: metafieldData.nit || "",
        fecha_nacimiento: metafieldData.fecha || "",
        permisosHutCoins: metafieldData.permisosHutCoins || false,
      };
    } catch (error) {
      // Si es un error que ya formateamos, relanzarlo
      if (
        error.message.includes("Email es requerido") ||
        error.message.includes("Número de celular") ||
        error.message.includes("Error de configuración") ||
        error.message.includes("Sesión expirada") ||
        error.message.includes("No tienes permisos") ||
        error.message.includes("Demasiadas solicitudes") ||
        error.message.includes("servidor está temporalmente") ||
        error.message.includes("Error de conexión") ||
        error.message.includes("Error al procesar") ||
        error.message.includes("Error al consultar") ||
        error.message.includes("correo electrónico ya está") ||
        error.message.includes("número de teléfono ya está") ||
        error.message.includes("Revisa tus datos") ||
        error.message.includes("No se pudo crear") ||
        error.message.includes("No se pudo obtener")
      ) {
        throw error;
      }

      console.log("ERROR CREACION CLIENTE - Error inesperado:", {
        timestamp: new Date().toISOString(),
        error: "Error no controlado en creación",
        errorName: error.name,
        errorMessage: error.message,
        datosCliente: datosCliente,
        stackTrace: error.stack,
        variables: variables,
      });

      // Manejar errores de red específicos
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new Error(
          "Error de conexión. Verifica tu conexión a internet e intenta nuevamente."
        );
      }

      if (error.name === "AbortError") {
        throw new Error(
          "La operación tardó demasiado tiempo. Por favor, intenta nuevamente."
        );
      }

      throw new Error(
        "Ha ocurrido un error al crear el cliente. Inténtalo de nuevo."
      );
    }
  }
}

class GestionOrdenesShopify {
  static async crearOrdenCompletada(data) {
    try {
      // ═══════════════════════════
      // OBTENER DATOS DEL USUARIO
      // ═══════════════════════════
      const usuario = JSON.parse(
        localStorage.getItem(
          localStorage.getItem("ph-tipo-usuario") == null
            ? "ph-datos-usuario"
            : "ph-datos-clienteFor-cc"
        )
      );

      var nexoDescuentoMonto = 0;
      var nexoDescuentoEtiqueta = 'NexoDiscount';
      var nexoDescuentoCodigos = 'NexoDiscount';
      try {
        nexoDescuentoMonto = parseFloat(localStorage.getItem('nexo_descuento_monto')) || 0;
        var _nexoEl = document.getElementById('nexo-descuento-etiqueta-resumen');
        if (_nexoEl && _nexoEl.textContent.trim()) nexoDescuentoEtiqueta = _nexoEl.textContent.trim();
        var _codigosRaw = sessionStorage.getItem('nexodiscount_preferred_codes');
        if (_codigosRaw) {
          var _codigos = JSON.parse(_codigosRaw);
          if (Array.isArray(_codigos) && _codigos.length > 0) {
            nexoDescuentoCodigos = _codigos.join(' - ');
          }
        }
      } catch (_e) {}

      const lineItems = data.carrito.informacionCompleta.items
        .map((item) => {
          let itemData = null;
          try {
            if (item.properties && item.properties.estructura) {
              itemData = JSON.parse(item.properties.estructura);
            }
          }
          catch (error) {
            console.error("Error al parsear estructura del item:", error);
          }

          if (!itemData || !itemData.item_base) {
            return null;
          }

          const cantidadBase = parseInt(itemData?.item_base?.cantidad || 1);
          const precioBase = parseFloat(itemData.item_base.precio || 0);
          const opcionesSeleccionadas = Array.isArray(itemData.items_principales?.productos)
            ? itemData.items_principales.productos
            : [];

          const opcionesConPrecio = opcionesSeleccionadas.filter((opcion) => parseFloat(opcion.precio || 0) > 0);

          const precioTotalOpcion = opcionesConPrecio.reduce((total, opcion) => {
            return total + (parseFloat(opcion.precio || 0) * parseInt(opcion.cantidad || 1));
          }, 0);

          const precioUnitarioTotal = precioBase + precioTotalOpcion;

          return {
            title: item.title || "Producto",
            quantity: cantidadBase,
            originalUnitPrice: precioUnitarioTotal.toFixed(2),
            requiresShipping: true,
            properties: [
              {
                name: "🛍️ OPCIONES PRINCIPALES",
                value: `${precioBase.toFixed(0)} Bs`,
              },
              ...opcionesSeleccionadas.map((opcion) => ({
                name: `▸ ${opcion.nombre}`,
                value: parseFloat(opcion.precio || 0) > 0
                  ? `${parseFloat(opcion.precio || 0).toFixed(0)} Bs`
                  : "Sin costo",
              })),
            ],
          };
        })
        .filter((item) => item !== null);

      // Calcular si necesita cargo por envio
      const costoEnvio =
        data.estadoPagina === "domicilio"
          ? parseFloat(window.costoEnvioDomicilio) || 0
          : 0.0;
      console.log("[crearOrdenCompletada] estadoPagina:", data.estadoPagina, "| costoEnvio:", costoEnvio, "| window.costoEnvioDomicilio:", window.costoEnvioDomicilio);

      const metodoPago = () => {
        switch (data.metodoPagoSeleccionado) {
          case "efectivo":
            return "Pago Efectivo";
          case "tarjeta":
            return "Pago Tarjeta";
          case "qr":
            return "Pago QR";
          default:
            return "Método de Pago Desconocido";
        }
      };

      const coordenadasFormatoEnviar = () => {
        const coordenadasData =
          data.estadoPagina == "domicilio"
            ? data.datosCheckout.metodo_envio_seleccionado.info_seleccionada
            : data.datosCheckout.metodo_envio_seleccionado.local_seleccionado;
        return `${coordenadasData.lat},${coordenadasData.lng}`;
      };

      const sucursal = () => {

        if (data.sucursal && data.sucursal.nombre) {
          return data.sucursal;
        }

        if (data.estadoPagina == "domicilio") {
          data.sucursal =
            AuxiliaresGlobal.obtenerSucursalTrabajo(
              data.datosCheckout.metodo_envio_seleccionado.info_seleccionada);
          return data.sucursal;
        } else {
          data.sucursal = data.localSeleccionado;
          return data.sucursal;
        }
      };

      var dirrecionEnvioAuxiliar = "";
      if (data.estadoPagina == "domicilio") {
        dirrecionEnvioAuxiliar = await AuxiliaresGlobal.obtenerDireccionShopify(
          data.direccionSeleccionado.lat,
          data.direccionSeleccionado.lng
        );
      } else {
        dirrecionEnvioAuxiliar = data.localSeleccionado.localizacion;
      }

      // ─── Dirección compartida (envío y facturación) ──────────────────
      const direccionShopify = {
        firstName: usuario.nombre,
        lastName: usuario.apellido,
        phone: usuario.celular,
        address1:
          data.estadoPagina == "domicilio"
            ? dirrecionEnvioAuxiliar.address1
            : dirrecionEnvioAuxiliar,
        address2:
          data.estadoPagina == "domicilio"
            ? dirrecionEnvioAuxiliar.address2
            : "",
        city:
          data.estadoPagina == "domicilio"
            ? dirrecionEnvioAuxiliar.city
            : "Santa Cruz",
        province:
          data.estadoPagina == "domicilio"
            ? dirrecionEnvioAuxiliar.province
            : "Andres Ibáñez, Santa Cruz de la Sierra",
        provinceCode:
          data.estadoPagina == "domicilio"
            ? dirrecionEnvioAuxiliar.provinceCode
            : "SC",
        countryCode:
          data.estadoPagina == "domicilio"
            ? dirrecionEnvioAuxiliar.countryCode
            : "BO",
        zip:
          data.estadoPagina == "domicilio"
            ? dirrecionEnvioAuxiliar.zip
            : "0000",
      };

      // ─── lineItems en formato DraftOrderLineItemInput ─────────────────
      // El precio total de la tarjeta principal se calcula con el base + cada opción seleccionada.
      const draftLineItems = lineItems.map((li) => ({
        title: li.title,
        quantity: li.quantity,
        requiresShipping: li.requiresShipping,
        originalUnitPrice: li.originalUnitPrice,
        customAttributes: (li.properties || []).map((p) => ({
          key: p.name,
          value: p.value === "" ? " " : p.value,
        })),
      }));

      // ─── Custom attributes de la orden ───────────────────────────────
      const ordenCustomAttributes = [
        { key: "DATOS CLIENTE", value: " " },
        { key: "Nombre", value: `${usuario.nombre} ${usuario.apellido}` },
        { key: "Email", value: usuario.email || " " },
        { key: "Celular", value: `+591 ${usuario.celular}` },
        { key: "CI", value: usuario.ci || " " },
        usuario.razon_social
          ? { key: "Razon Social", value: usuario.razon_social }
          : null,
        usuario.nit ? { key: "NIT", value: usuario.nit } : null,
        data.metodo_pago == "tarjeta" && data.datosCheckout.info_metodo_pago_seleccionado
          ? {
            key: "Tarjeta Primeros 4 Digitos",
            value:
              data.datosCheckout.info_metodo_pago_seleccionado
                ?.tarjeta_primera_4_digitos || " ",
          }
          : null,
        data.metodo_pago == "tarjeta" && data.datosCheckout.info_metodo_pago_seleccionado
          ? {
            key: "Tarjeta Ultimos 4 Digitos",
            value:
              data.datosCheckout.info_metodo_pago_seleccionado
                ?.tarjeta_segundo_4_digitos || " ",
          }
          : null,
        { key: "Metodo Pago", value: metodoPago() },
        nexoDescuentoMonto > 0
          ? { key: "Descuento NexoDiscount", value: `${nexoDescuentoEtiqueta} -Bs ${nexoDescuentoMonto.toFixed(2)}` }
          : null,
        data.metodo_pago == "qr"
          ? { key: "ID QR", value: data.idQr || " " }
          : null,
        {
          key: "Metodo Entrega",
          value:
            data.estadoPagina === "domicilio"
              ? "Envío a Domicilio"
              : "Recojo en Local",
        },
        ...(data.estadoPagina === "domicilio"
          ? [
            { key: "Alias", value: data.direccionSeleccionado?.alias || " " },
            { key: "Indicaciones Direccion", value: data.direccionSeleccionado?.indicaciones || " " },
          ]
          : []),
        { key: "Local Designado", value: sucursal()?.nombre || "No especificado" },
        { key: "C.C Coordenadas", value: coordenadasFormatoEnviar() },
        { key: "C.C Google Maps", value: `https://www.google.com/maps?q=${coordenadasFormatoEnviar()}` },
        {
          key: "Detalle Pedido",
          value: JSON.stringify(
            data.carrito.informacionCompleta.items.map((item, index) => {
              let itemEstructura = null;
              try {
                if (item.properties && item.properties.estructura) {
                  itemEstructura = JSON.parse(item.properties.estructura);
                  var item_base = new ItemRecontruccion({
                    idshopify: itemEstructura.item_base.idshopify,
                    idtrabajo: itemEstructura.item_base.idtrabajo,
                    cantidad: itemEstructura.item_base.cantidad,
                  });
                  var items_principales = null;
                  if (itemEstructura.items_principales) {
                    items_principales = {
                      titulo: itemEstructura.items_principales.titulo,
                      producto: itemEstructura.items_principales.productos?.map((opcion) =>
                        new ItemRecontruccion({
                          idshopify: opcion.idshopify,
                          idtrabajo: opcion.idtrabajo,
                          cantidad: opcion.cantidad,
                        })
                      ) || [],
                    };
                  }
                  return new ProductoCarrito({
                    producto: item_base,
                    items_principales: items_principales,
                    colecciones_trabajo: itemEstructura.colecciones_trabajo,
                  });
                } else {
                  return `❌ ${index + 1}: Sin estructura de datos\n\n`;
                }
              } catch (error) {
                return `❌ ${index + 1}: Error al procesar\n\n`;
              }
            })
          ),
        },
      ].filter((attr) => attr !== null && attr.value !== null && attr.value !== undefined);

      // ─── Metafields (se aplican en paso 4 vía orderUpdate) ───────────
      const metafieldsOrden = [
        {
          namespace: "estructura",
          key: "datos_proceso_checkout",
          type: "json_string",
          value: JSON.stringify(data.datosCheckout),
        },
        {
          namespace: "estructura",
          key: "datos_carrito_proceso",
          type: "json_string",
          value: JSON.stringify(
            data.carrito.informacionCompleta.items.map((item) => ({
              id: item.id,
              title: item.title,
              quantity: item.quantity,
              variant_id: item.variant_id,
              key: item.key,
              price: item.price,
              original_price: item.original_price,
              presentment_price: item.presentment_price,
              properties: item.properties || {},
            }))
          ),
        },
      ];

      // ─── Input del borrador ──────────────────────────────────────────
      const draftInput = {
        note:
          !data.datosCheckout.nota_para_envio || data.datosCheckout.nota_para_envio.trim() == ""
            ? "Sin nota"
            : data.datosCheckout.nota_para_envio.trim(),
        lineItems: draftLineItems,
        customAttributes: ordenCustomAttributes,
        shippingAddress: direccionShopify,
        billingAddress: direccionShopify,
        customerId: usuario.id,
        taxExempt: true,
        ...(costoEnvio > 0 && {
          shippingLine: { title: "Envío a Domicilio", price: costoEnvio.toFixed(2) },
        }),
        ...(nexoDescuentoMonto > 0 && {
          appliedDiscount: {
            title: nexoDescuentoCodigos,
            value: nexoDescuentoMonto,
            valueType: "FIXED_AMOUNT",
          },
        }),
      };

      console.log("[crearOrdenCompletada] draftInput.shippingLine:", draftInput.shippingLine ?? "(no shipping)");
      console.log("[crearOrdenCompletada] draftInput.appliedDiscount:", draftInput.appliedDiscount ?? "(sin descuento)");

      // ═══════════════════════════════════════════════════════════════
      // PASO 1 — draftOrderCreate
      // ═══════════════════════════════════════════════════════════════
      const draftResp = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: `
            mutation draftOrderCreate($input: DraftOrderInput!) {
              draftOrderCreate(input: $input) {
                draftOrder { id }
                userErrors { field message }
              }
            }
          `,
          variables: { input: draftInput },
        }),
      });
      const draftJson = await draftResp.json();
      console.log("draftOrderCreate:", draftJson);
      if (draftJson.errors) {
        console.error("Errores GraphQL (draftOrderCreate):", draftJson.errors);
        return { success: false, errors: draftJson.errors };
      }
      const draftResult = draftJson.data.draftOrderCreate;
      if (draftResult.userErrors.length > 0) {
        console.error("Errores usuario (draftOrderCreate):", draftResult.userErrors);
        return { success: false, errors: draftResult.userErrors };
      }
      const draftOrderId = draftResult.draftOrder.id;

      // ═══════════════════════════════════════════════════════════════
      // PASO 2 — draftOrderComplete
      // ═══════════════════════════════════════════════════════════════
      const completeResp = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: `
            mutation draftOrderComplete($id: ID!) {
              draftOrderComplete(id: $id) {
                draftOrder {
                  order {
                    id
                    name
                    email
                    totalPrice
                    createdAt
                    totalTaxSet { shopMoney { amount currencyCode } }
                    shippingAddress { address1 city provinceCode countryCode zip }
                    billingAddress  { address1 city provinceCode countryCode zip }
                    lineItems(first: 5) {
                      nodes {
                        id title quantity
                        taxLines { title rate priceSet { shopMoney { amount currencyCode } } }
                      }
                    }
                  }
                }
                userErrors { field message }
              }
            }
          `,
          variables: { id: draftOrderId },
        }),
      });
      const completeJson = await completeResp.json();
      console.log("draftOrderComplete:", completeJson);
      if (completeJson.errors) {
        console.error("Errores GraphQL (draftOrderComplete):", completeJson.errors);
        return { success: false, errors: completeJson.errors };
      }
      const completeResult = completeJson.data.draftOrderComplete;
      if (completeResult.userErrors.length > 0) {
        console.error("Errores usuario (draftOrderComplete):", completeResult.userErrors);
        return { success: false, errors: completeResult.userErrors };
      }
      const orden = completeResult.draftOrder.order;
      const orderId = orden.id;

      // Nota: draftOrderComplete (sin paymentPending: true) ya deja la orden como
      // PAGADA, por lo que no se necesita una mutación orderMarkAsPaid adicional.

      // ═══════════════════════════════════════════════════════════════
      // PASO 3 — orderUpdate (metafields)
      // ═══════════════════════════════════════════════════════════════
      const updateResp = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: `
            mutation orderUpdate($input: OrderInput!) {
              orderUpdate(input: $input) {
                order { id }
                userErrors { field message }
              }
            }
          `,
          variables: { input: { id: orderId, metafields: metafieldsOrden } },
        }),
      });
      const updateJson = await updateResp.json();
      console.log("orderUpdate (metafields):", updateJson);
      if (updateJson.errors) {
        console.warn("No se pudieron guardar los metafields:", updateJson.errors);
      }

      console.log("Orden creada con éxito:", orden);
      return {
        success: true,
        order: orden,
        nombre: orden.name,
      };
    } catch (error) {
      console.error("Error general al crear orden:", error);
      return { success: false, error: error.message };
    }
  }

  static async obtenerFulfillmentOrders(orderId, first) {
    try {
      const query = `
      query {
        order(id: "${orderId}") {
          fulfillmentOrders(first: ${first}) {
            edges {
              node {
                id
                assignedLocation {
                  location {
                    id
                    name
                  }
                }
                status
              }
            }
          }
        }
      }
    `;

      const response = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query }),
      });

      const data = await response.json();

      if (data.errors) {
        console.error("Errores en GraphQL:", data.errors);
        return null;
      }

      if (!data.data || !data.data.order) {
        console.error("No se encontró la orden");
        return null;
      }

      // Extraer solo el ID del primer fulfillment order
      const fulfillmentOrders = data.data.order.fulfillmentOrders.edges;
      if (fulfillmentOrders.length === 0) {
        console.error("No se encontraron fulfillment orders");
        return null;
      }

      return fulfillmentOrders[0].node.id;
    } catch (error) {
      console.error("Error al obtener fulfillment orders:", error);
      return null;
    }
  }

  static async moverFulfillmentOrder(fulfillmentOrderId, newLocationId) {
    try {
      const mutation = `
      mutation {
        fulfillmentOrderMove(
          id: "${fulfillmentOrderId}",
          newLocationId: "${newLocationId}"
        ) {
          movedFulfillmentOrder {
            id
            status
          }
          originalFulfillmentOrder {
            id
            status
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

      const response = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: mutation }),
      });

      const data = await response.json();

      if (data.errors) {
        console.error("moverFulfillmentOrder - Errores GraphQL:", JSON.stringify(data.errors), "| id:", fulfillmentOrderId, "| newLocationId:", newLocationId);
        return null;
      }

      const resultado = data.data?.fulfillmentOrderMove;
      if (resultado?.userErrors?.length > 0) {
        // La orden ya está asignada a la sucursal destino: no hay nada que mover,
        // se considera éxito.
        const yaEnDestino = resultado.userErrors.every((e) =>
          /current origin location/i.test(e?.message || "")
        );
        if (yaEnDestino) {
          console.log("moverFulfillmentOrder - la orden ya está en la ubicación destino, no se requiere mover.");
          return true;
        }
        console.error("moverFulfillmentOrder - userErrors:", JSON.stringify(resultado.userErrors), "| id:", fulfillmentOrderId, "| newLocationId:", newLocationId);
        return null;
      }

      return true;
    } catch (error) {
      console.error("Error al mover fulfillment order:", error);
      return null;
    }
  }

  static async crearOrdenOfiSistema(infoTrabajo) {

    infoTrabajo.id_sucursal = parseInt(
      infoTrabajo.id_sucursal.split("/").pop().match(/\d+/)?.[0] ||
      "0"
    ).toString();

    infoTrabajo.precio_total_orden = parseInt(
      infoTrabajo.precio_total_orden.replace("Bs ", "").replace(",", "")
    );

    // Constantes
    const API_URL =
      "https://apicloud.farmacorp.com/shopifygraphql/api/ShopifyGraphQL/forward-order";

    // Funcion para manejar errores
    const manejarErrores = (error, mensaje, detalles = {}) => {
      const errorLog = {
        timestamp: new Date().toISOString(),
        mensaje,
        detalles,
        error: error.message,
        stack: error.stack,
      };

      console.error(
        "Error en la API de OfiSistema (crearOrdenOfiSistema):",
        JSON.stringify(errorLog, null, 2)
      );
      throw new Error(`Error al procesar la orden: ${mensaje}`);
    };

    try {
      // Validaciones iniciales
      if (!infoTrabajo) {
        throw new Error("No se proporcionó información de trabajo");
      }

      // Funcion auxiliar para formatear productos con validacion
      const formatearProducto = (itemCarrito, metodoEntrega) => {
        try {
          const detalleItem =
            typeof itemCarrito === "string"
              ? JSON.parse(itemCarrito)
              : itemCarrito;

          // Validaciones de estructura
          if (!detalleItem.item_base?.idtrabajo) {
            throw new Error("Producto invalido : falta idTrabajo");
          }

          // Verificar si tiene items principales
          const tieneItemsPrincipales =
            detalleItem.items_principales?.productos?.length > 0;

          const esProductoSimple = !tieneItemsPrincipales;
          const variationsChoices = [[]];

          // Solo procesar variaciones si hay items principales
          if (tieneItemsPrincipales) {
            const productos = detalleItem.items_principales.productos;

            // Verificar si es una pizza usando colecciones_trabajo
            const esPizza = detalleItem.colecciones_trabajo?.some(coleccion =>
              coleccion.includes("Pizza Clasica") || coleccion.includes("Pizza Premium") ||
              coleccion.includes("Pizza Grande") || coleccion.includes("Pizza Familiar")
            );

            if (esPizza) {
              // CASO PIZZA: Buscar el tamaño primero
              const productoTamano = productos.find(p =>
                p.titulo?.includes("Tamaño")
              );

              // Todos los demás productos (no tamaño) van dentro del tamaño
              const productosExtras = productos.filter(p =>
                !p.titulo?.includes("Tamaño")
              );

              if (productoTamano) {
                // Crear el objeto del tamaño
                const objetoTamano = {
                  itemId: `${infoTrabajo.metodo_entrega}${productoTamano.idtrabajo}`,
                  count: 1,
                  title: {
                    en_US: null,
                    es_ES: productoTamano.nombre,
                  },
                  desc: {
                    en_US: null,
                    en_ID: `co-${productoTamano.idtrabajo}-1`,
                    integrationNumber: productoTamano.idtrabajo,
                    taxCode: null,
                    es_ES: "",
                  },
                  counter: "75c860b5-1392-d9ed-6a73-fb7b85abb4d8",
                  price: `${productoTamano.precio * 100}`,
                  variations: [],
                  variationsChoices: [],
                  html: true,
                  precioLabel: null,
                  media: null,
                };

                // Si hay productos extras, agregarlos dentro del tamaño
                if (productosExtras.length > 0) {
                  objetoTamano.variationsChoices = [
                    productosExtras.map(producto => ({
                      itemId: `${infoTrabajo.metodo_entrega}${producto.idtrabajo}`,
                      count: "1",
                      title: {
                        en_US: null,
                        es_ES: producto.nombre,
                      },
                      desc: {
                        en_US: null,
                        en_ID: `co-${producto.idtrabajo}-1`,
                        integrationNumber: `C${producto.idtrabajo}`,
                        taxCode: null,
                        es_ES: "",
                      },
                      counter: "75c860b5-1392-d9ed-6a73-fb7b85abb4d8",
                      price: `${producto.precio * 100}`,
                      variations: [],
                      variationsChoices: [],
                      html: true,
                      precioLabel: null,
                      media: null,
                    }))
                  ];
                }

                variationsChoices[0].push(objetoTamano);
              }
            } else {
              // CASO OTROS PRODUCTOS: Todos al mismo nivel
              productos.forEach(opcion => {
                variationsChoices[0].push({
                  itemId: `${infoTrabajo.metodo_entrega}${opcion.idtrabajo}`,
                  count: 1,
                  title: {
                    en_US: null,
                    es_ES: opcion.nombre,
                  },
                  desc: {
                    en_US: null,
                    en_ID: `co-${opcion.idtrabajo}-1`,
                    integrationNumber: opcion.idtrabajo,
                    taxCode: null,
                    es_ES: "",
                  },
                  counter: "75c860b5-1392-d9ed-6a73-fb7b85abb4d8",
                  price: `${opcion.precio * 100}`,
                  variations: [],
                  variationsChoices: [],
                  html: true,
                  precioLabel: null,
                  media: null,
                });
              });
            }
          }

          return {
            tictukItemId: `${infoTrabajo.metodo_entrega}${detalleItem.item_base.idtrabajo}`,
            integrationId: esProductoSimple
              ? detalleItem.item_base.idtrabajo
              : `${detalleItem.item_base.idtrabajo}|CM${detalleItem.item_base.obj_num}`,
            taxCode: null,
            title: detalleItem.item_base.nombre,
            variations: [],
            variationsChoices:
              variationsChoices[0].length > 0 ? variationsChoices : [],
            price: `${detalleItem.item_base.precio * 100}`,
            totalPrice: `${detalleItem.item_base.precio * 100}`,
          };
        } catch (error) {
          manejarErrores(error, "Error al formatear producto", { itemCarrito });
        }
      };

      // Formatear direccion de entrega con validacion
      const formatearDireccion = (direccionEnvio) => {
        try {
          console.log("Direccion envio: ", direccionEnvio);

          if (
            !direccionEnvio?.coordenadas?.lat ||
            !direccionEnvio?.coordenadas?.lng
          ) {
            throw new Error("Coordenadas de entrega invalidas");
          }

          return {
            formatted: direccionEnvio.direccion.replaceAll(", Bolivia", ""),
            countryCode: "BO",
            city: "Santa Cruz de la Sierra",
            street: "",
            number: "0",
            apt: null,
            floor: null,
            entrance: null,
            pobox: null,
            comment: `${direccionEnvio.alias || ""}, ${direccionEnvio.indicaciones || ""
              }`.trim(),
            latLng: {
              lat: direccionEnvio.coordenadas.lat,
              lng: direccionEnvio.coordenadas.lng,
            },
            approximate: "false",
            postalCode: null,
            additionalInfo: ``,
          };
        } catch (error) {
          manejarErrores(error, "Error al formatear dirección", {
            direccionEnvio,
            direccion,
          });
        }
      };

      const productos_enFormato_ofiSistema = [];

      infoTrabajo.productos.forEach((item) => {
        const detalleItem = typeof item === "string" ? JSON.parse(item) : item;
        const cantidad = detalleItem.item_base?.cantidad || 1;

        for (let i = 0; i < cantidad; i++) {
          productos_enFormato_ofiSistema.push(
            formatearProducto(item, infoTrabajo.metodo_entrega)
          );
        }
      });

      // Validar precio total
      if (typeof infoTrabajo.precio_total_orden !== "number") {
        throw new Error("Precio total inválido");
      }
      
      const nexoMonto = parseFloat(localStorage.getItem('nexo_descuento_monto')) || 0;
      
      const payload = {
        tictukOrderId: `${infoTrabajo.id_orden}`,
        developer: "com.ph.web",
        tictukStoreId: "753d6cde-1f9b-1e9f-70cd-fc048eb25ce0",
        locale: "es_ES",
        currency: "BOB",
        apiStoreId: infoTrabajo.id_sucursal,
        contact: {
          firstName: infoTrabajo.nombre || "",
          lastName: infoTrabajo.apellido || "",
          phone: infoTrabajo.telefono || "",
          email: infoTrabajo.correo || "",
        },
        orderType: infoTrabajo.metodo_entrega,
        tip: "0",
        tableId: "",
        orderItems: productos_enFormato_ofiSistema,
        comment: `PAGO : ${infoTrabajo.metodo_pago.toUpperCase()} | TEL : ${infoTrabajo.telefono} | NIT: ${infoTrabajo.nit || ""} - ${infoTrabajo.razon_social || ""
          }`,
        price: `${infoTrabajo.precio_total_orden * 100}`,
        timezone: "America/La_Paz",
        paymentMethod: infoTrabajo.metodo_pago == "efectivo" ? "cash" : infoTrabajo.metodo_pago == "tarjeta" ? "credit" : infoTrabajo.metodo_pago == "qr" ? "Pago QR" : "credit", onlinePayment: "false",
        deliveryCharge:
          infoTrabajo.metodo_entrega === "DELIVERY"
            ? `${parseInt(window.costoEnvioDomicilio) * 100}`
            : "0",
        deliveredBy: new Date().toISOString().slice(0, 19).replace("T", " "),
        address:
          infoTrabajo.metodo_entrega === "DELIVERY"
            ? formatearDireccion(infoTrabajo.direccion_envio)
            : [],
        pickupBy: null,
        paymentGatewayApprovalID: null,
        paymentGatewayName: null,
        cardType: null,
        AuthorizationCode: null,
        chargesAndDiscounts: null,
        //chargesAndDiscounts: `[{${Math.round(nexoMonto * 100)}}]`,
        /*chargesAndDiscounts: (() => {
          try {
            var nexoMonto = parseFloat(localStorage.getItem('nexo_descuento_monto')) || 0;
            if (!nexoMonto || nexoMonto <= 0) return null;
            var etiquetaEl = document.getElementById('nexo-descuento-etiqueta-resumen');
            var etiquetaTexto = etiquetaEl ? etiquetaEl.textContent.trim() : 'NexoDiscount';
            return [
              {
                amount: `-${Math.round(nexoMonto * 100)}`,
                title: `Desc. ${etiquetaTexto}`,
                type: 'discount',
              },
            ];
          } catch (e) {
            return null;
          }
        })(),*/
        dynamicAnswers: null,
        sduplicate: false,
        chargeAmount: `-${Math.round(nexoMonto * 100)}`,
        channel: "Web",
        note: null,
        subOrderType: null,
        HostAuthorizationCode: null,
        TerminalId: null,
        MerchantId: null,
        BatchNumber: null,
      };

      // // Crear un blob con el contenido JSON
      // const contenidoJSON = JSON.stringify(payload, null, 2);
      // const blob = new Blob([contenidoJSON], { type: 'application/json' });

      // // Crear un enlace temporal para la descarga
      // const url = window.URL.createObjectURL(blob);
      // const enlaceDescarga = document.createElement('a');
      // enlaceDescarga.href = url;
      // enlaceDescarga.download = `orden-${payload.tictukOrderId || 'pedido'}.json`;

      // // Simular clic y limpiar
      // document.body.appendChild(enlaceDescarga);
      // enlaceDescarga.click();
      // document.body.removeChild(enlaceDescarga);
      // window.URL.revokeObjectURL(url);

      // return;

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log(
        "Respuesta de la API de OfiSistema (crearOrdenOfiSistema):",
        response
      );

      if (!response.ok) {
        throw new Error(
          `Error en la respuesta del servidor: ${response.status}`
        );
      }

      const result = await response.text();
      return result;
    } catch (error) {
      manejarErrores(error, "Error general al crear orden", {
        metodoEntrega: infoTrabajo?.metodo_entrega,
        idOrden: infoTrabajo?.id_orden,
      });
    }
  }

  static async crearPreOrden() { }

  static async completarOrdenToPagada() { }

  static async cambiarPedidoAbandonado() { }

  static async getDetalleCompletoOrdenLocal(id_orden) {
    try {
      // URL del endpoint
      const url = `http://localhost:3001/orden/${id_orden}`;

      console.log(`📋 Obteniendo orden: ${id_orden}`);
      console.log(`🔗 Backend: http://localhost:3001`);

      // Hacer petición al backend
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Error HTTP: ${response.status} - ${response.statusText}`
        );
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Error desconocido del backend");
      }

      // Los datos vienen en result.data con el formato MaquetacionOrden
      const ordenData = result.data;

      console.log("✅ Orden obtenida exitosamente del backend");
      console.log("ordenData: ", ordenData);

      // Retornar nueva instancia de OrdenShopify con los datos obtenidos
      return new MaquetacionOrden({
        // INFORMACIÓN BÁSICA
        id: orden.id || "",
        id: ordenData.id,
        nro: ordenData.nro,
        total: ordenData.total,
        estado: ordenData.estado,
        tiempo_creacion: ordenData.tiempo_creacion,
        total_items: ordenData.total_items,
        nota: ordenData.nota,
        line_items: ordenData.line_items.map((item) => {
          return new LineItemOfOrden({
            nombre: item.nombre,
            precio: item.precio,
            cantidad: item.cantidad,
            properties: item.properties || [],
          });
        }),
        // CUSTOM ATTRIBUTES
        email: ordenData.email,
        telefono: ordenData.telefono,
        ci: ordenData.ci,
        nit: ordenData.nit,
        razon_social: ordenData.razon_social,
        metodo_pago: ordenData.metodo_pago,
        metodo_entrega: ordenData.metodo_entrega,
        alias: ordenData.alias,
        indicaciones: ordenData.indicaciones,
        local_designado: ordenData.local_designado,
        cc_coordenadas: ordenData.cc_coordenadas,
        cc_google_maps: ordenData.cc_google_maps,
        detalle_reconstruccion: ordenData.detalle_reconstruccion,

        // ESTADÍSTICAS ADICIONALES
        total_properties: ordenData.total_properties,
        items_con_estructura: ordenData.items_con_estructura,
        total_opciones: ordenData.total_opciones,
      });
    } catch (error) {
      console.error("❌ Error obteniendo orden del backend:", error.message);
      throw error;
    }
  }

  static async getDetalleCompletoOrdenREST(id_orden) {
    try {
      console.log(`📋 Obteniendo orden GraphQL: ${id_orden}`);

      // OPCIÓN 1: Si tu endpoint es GraphQL Admin API
      const query = `
        query getOrder($id: ID!) {
          order(id: $id) {
            id
            name
            totalPriceSet {
              shopMoney {
                amount
                currencyCode
              }
            }
            createdAt
            email
            note
            customAttributes {
              key
              value
            }
            lineItems(first: 100) {
              edges {
                node {
                  id
                  title
                  quantity
                  originalUnitPriceSet {
                    shopMoney {
                      amount
                      currencyCode
                    }
                  }
                  customAttributes {
                    key
                    value
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        id: `gid://shopify/Order/${id_orden}`,
      };

      console.log("🔗 URL:", window.urlConsulta);

      const response = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Error HTTP: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("📦 Data recibida:", data);

      // Verificar errores de GraphQL
      if (data.errors) {
        console.error("❌ GraphQL Errors:", data.errors);
        throw new Error(`GraphQL Error: ${data.errors[0].message}`);
      }

      if (!data.data) {
        console.error("❌ No data field in response");
        throw new Error("Respuesta GraphQL inválida");
      }

      if (!data.data.order) {
        console.error("❌ Order not found in GraphQL response");
        // FALLBACK: Intentar con REST API
        return await this.getDetalleCompletoOrdenRESTFallback(id_orden);
      }

      const orden = data.data.order;
      console.log("✅ Orden encontrada:", orden.name);

      // Procesar customAttributes
      const attrs = {};
      if (orden.customAttributes) {
        orden.customAttributes.forEach(
          (attr) => (attrs[attr.key] = attr.value)
        );
      }

      // Procesar line items
      let totalProperties = 0;
      let itemsConEstructura = 0;
      let totalOpciones = 0;

      const lineItems = orden.lineItems.edges.map((edge) => {
        const item = edge.node;

        if (item.customAttributes) {
          totalProperties += item.customAttributes.length;

          const estructura = item.customAttributes.find(
            (p) => p.key === "estructura"
          );
          if (estructura && estructura.value) {
            try {
              const est = JSON.parse(estructura.value);
              itemsConEstructura++;
              if (est.items_principales?.productos) {
                totalOpciones += est.items_principales.productos.length;
              }
            } catch (e) {
              console.warn("Error parseando estructura:", e);
            }
          }
        }

        return new LineItemOfOrden({
          nombre: item.title,
          precio: parseFloat(item.originalUnitPriceSet?.shopMoney?.amount || 0),
          cantidad: item.quantity,
          properties: item.customAttributes || [],
        });
      });

      console.log(
        "Testin de pruebas :",
        new MaquetacionOrden({
          // INFORMACIÓN BÁSICA
          nro: orden.name || "",
          total: parseFloat(orden.totalPriceSet?.shopMoney?.amount || 0),
          estado: orden.financialStatus || "",
          tiempo_creacion: orden.createdAt || "",
          total_items: lineItems.length || 0,
          nota: orden.note || "",
          line_items: lineItems,

          // CUSTOM ATTRIBUTES
          email: orden.email || attrs["Email"] || "",
          telefono: attrs["Celular"] || "",
          ci: attrs["CI"] || "",
          nit: attrs["NIT"] || "",
          razon_social: attrs["Razon Social"] || "",
          metodo_pago: attrs["Metodo Pago"] || "",
          metodo_entrega: attrs["Metodo Entrega"] || "",
          alias: attrs["Alias"] || "",
          indicaciones: attrs["Indicaciones Direccion"] || "",
          local_designado: attrs["Local Designado"] || "",
          cc_coordenadas: attrs["C.C Coordenadas"] || "",
          cc_google_maps: attrs["C.C Google Maps"] || "",
          detalle_reconstruccion: attrs["Detalle Pedido"] || "",

          // ESTADÍSTICAS
          total_properties: totalProperties,
          items_con_estructura: itemsConEstructura,
          total_opciones: totalOpciones,
        })
      );

      return new MaquetacionOrden({
        // INFORMACIÓN BÁSICA
        nro: orden.name || "",
        total: parseFloat(orden.totalPriceSet?.shopMoney?.amount || 0),
        estado: orden.financialStatus || "",
        tiempo_creacion: orden.createdAt || "",
        total_items: lineItems.length || 0,
        nota: orden.note || "",
        line_items: lineItems,

        // CUSTOM ATTRIBUTES
        email: orden.email || attrs["Email"] || "",
        telefono: attrs["Celular"] || "",
        ci: attrs["CI"] || "",
        nit: attrs["NIT"] || "",
        razon_social: attrs["Razon Social"] || "",
        metodo_pago: attrs["Metodo Pago"] || "",
        metodo_entrega: attrs["Metodo Entrega"] || "",
        alias: attrs["Alias"] || "",
        indicaciones: attrs["Indicaciones Direccion"] || "",
        local_designado: attrs["Local Designado"] || "",
        cc_coordenadas: attrs["C.C Coordenadas"] || "",
        cc_google_maps: attrs["C.C Google Maps"] || "",
        detalle_reconstruccion: attrs["Detalle Pedido"] || "",

        // ESTADÍSTICAS
        total_properties: totalProperties,
        items_con_estructura: itemsConEstructura,
        total_opciones: totalOpciones,
      });
    } catch (error) {
      console.error("❌ Error obteniendo orden GraphQL:", error.message);
      console.error("📍 Stack:", error.stack);

      // FALLBACK: Intentar con REST API
      try {
        console.log("🔄 Intentando fallback con REST API...");
        return await this.getDetalleCompletoOrdenRESTFallback(id_orden);
      } catch (fallbackError) {
        console.error("❌ Fallback también falló:", fallbackError.message);
        throw new Error(
          `GraphQL failed: ${error.message}. REST fallback failed: ${fallbackError.message}`
        );
      }
    }
  }

  // FALLBACK deshabilitado: llamaba directamente a la Admin API de Shopify con un
  // token embebido en el cliente. Toda consulta debe pasar por el backend propio
  // (window.urlConsulta) que agrega la autenticación del lado del servidor.
  static async getDetalleCompletoOrdenRESTFallback(id_orden) {
    throw new Error(
      "Fallback REST no disponible: las consultas de órdenes deben realizarse vía GraphQL a través del backend."
    );
  }

  static async getOrdenesCliente(customerId, pageSize = 250, cursor = null, allOrders = []) {
    try {
      // Validar que se recibió el customerId
      if (!customerId) {
        throw new Error("El ID del cliente es requerido");
      }

      // Construir el customerId en formato GraphQL si no viene con el prefijo
      const customerIdFormatted = customerId.startsWith("gid://shopify/Customer/")
        ? customerId
        : `gid://shopify/Customer/${customerId}`;

      // Query GraphQL corregida con campos válidos de Shopify
      const query = `
        query getCustomerOrders($customerId: ID!, $pageSize: Int!, $cursor: String) {
          customer(id: $customerId) {
            id
            firstName
            lastName
            email
            orders(first: $pageSize, after: $cursor, sortKey: CREATED_AT, reverse: true) {
              pageInfo {
                hasNextPage
                endCursor
              }
              nodes {
                id
                name
                displayFinancialStatus
                displayFulfillmentStatus
                totalPrice
                currencyCode
                createdAt
                updatedAt
                processedAt
                cancelledAt
                cancelReason
                closedAt
                tags
                note
                customAttributes {
                  key
                  value
                }
                shippingAddress {
                  address1
                  address2
                  city
                  province
                  country
                  zip
                  firstName
                  lastName
                  phone
                }
                billingAddress {
                  address1
                  address2
                  city
                  province
                  country
                  zip
                  firstName
                  lastName
                  phone
                }
                lineItems(first: 100) {
                  edges {
                    node {
                      id
                      title
                      quantity
                      currentQuantity
                      originalUnitPriceSet {
                        shopMoney {
                          amount
                          currencyCode
                        }
                      }
                      variant {
                        id
                        title
                        price
                        sku
                        image {
                          url
                        }
                      }
                      product {
                        id
                        title
                        handle
                        featuredImage {
                          url
                        }
                        images(first: 1) {
                          edges {
                            node {
                              url
                            }
                          }
                        }
                      }
                      customAttributes {
                        key
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        customerId: customerIdFormatted,
        pageSize: pageSize,
        cursor: cursor,
      };

      // Realizar petición a Shopify
      const response = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Error HTTP: ${response.status} - ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log('data', data);

      // Verificar errores de GraphQL
      if (data.errors) {
        console.error("❌ Errores GraphQL:", data.errors);
        throw new Error(
          data.errors.map((e) => e.message).join(", ") || "Error en la consulta GraphQL"
        );
      }

      if (!data.data?.customer?.orders?.nodes) {
        console.warn("⚠️ No se encontraron órdenes para el cliente");
        return allOrders.length > 0 ? allOrders : [];
      }

      const orders = data.data.customer.orders.nodes;
      const pageInfo = data.data.customer.orders.pageInfo;
      const customerEmail = data.data.customer.email || "";


      // Mapear TODAS las órdenes (sin filtrar)
      const ordenesMapeadas = orders.map((orden) => {
        // Procesar customAttributes de la orden
        const attrs = {};
        if (orden.customAttributes) {
          orden.customAttributes.forEach(
            (attr) => (attrs[attr.key] = attr.value)
          );
        }

        // Procesar line items
        let totalProperties = 0;
        let itemsConEstructura = 0;
        let totalOpciones = 0;

        const lineItems = orden.lineItems.edges.map((edge, index) => {
          const item = edge.node;

          if (item.customAttributes) {
            totalProperties += item.customAttributes.length;

            const estructura = item.customAttributes.find(
              (p) => p.key === "estructura"
            );
            if (estructura && estructura.value) {
              try {
                const est = JSON.parse(estructura.value);
                itemsConEstructura++;
                if (est.items_principales?.productos) {
                  totalOpciones += est.items_principales.productos.length;
                }
              } catch (e) {
                console.warn("Error parseando estructura:", e);
              }
            }
          }

          // Obtener imagen solo del primer producto (index === 0)
          let imagen = "";
          if (index === 0) {
            // Prioridad: variant.image > product.featuredImage > product.images[0]
            imagen = item.variant?.image?.url
              || item.product?.featuredImage?.url
              || item.product?.images?.edges?.[0]?.node?.url
              || "";
          }

          return new LineItemOfOrden({
            nombre: item.title || "",
            precio: parseFloat(item.originalUnitPriceSet?.shopMoney?.amount || 0),
            cantidad: item.quantity || 0,
            imagen: imagen,
            properties: item.customAttributes || [],
          });
        });

        // Construir objeto MaquetacionOrden
        return new MaquetacionOrden({
          // INFORMACIÓN BÁSICA
          id: orden.id || "",
          nro: orden.name || "",
          total: parseFloat(orden.totalPrice || 0),
          estado: orden.displayFinancialStatus || "",
          tiempo_creacion: orden.createdAt || "",
          total_items: lineItems.length || 0,
          nota: orden.note || "",
          line_items: lineItems,

          // CUSTOM ATTRIBUTES
          email: customerEmail || attrs["Email"] || "",
          telefono: attrs["Celular"] || "",
          ci: attrs["CI"] || "",
          nit: attrs["NIT"] || "",
          razon_social: attrs["Razon Social"] || "",
          metodo_pago: attrs["Metodo Pago"] || "",
          metodo_entrega: attrs["Metodo Entrega"] || "",
          alias: attrs["Alias"] || "",
          indicaciones: attrs["Indicaciones Direccion"] || orden.shippingAddress?.address1 || "",
          local_designado: attrs["Local Designado"] || "",
          cc_coordenadas: attrs["C.C Coordenadas"] || "",
          cc_google_maps: attrs["C.C Google Maps"] || "",
          detalle_reconstruccion: attrs["Detalle Pedido"] || "",

          // ESTADÍSTICAS
          total_properties: totalProperties,
          items_con_estructura: itemsConEstructura,
          total_opciones: totalOpciones,
        });
      });

      // Agregar las órdenes mapeadas al array acumulativo
      allOrders = allOrders.concat(ordenesMapeadas);

      // Paginación recursiva: si hay más páginas, obtenerlas
      if (pageInfo.hasNextPage && pageInfo.endCursor) {
        console.log(`ℹ️ Hay más órdenes disponibles. Obteniendo siguiente página con cursor: ${pageInfo.endCursor}`);
        return await this.getOrdenesCliente(
          customerId,
          pageSize,
          pageInfo.endCursor,
          allOrders
        );
      }

      // console.log(`✅ Proceso completado. Total de órdenes obtenidas: ${allOrders.length}`);
      console.log('📦 Primeras 5 órdenes:', allOrders.slice(0, 5));
      return allOrders;
    } catch (error) {
      console.error("❌ Error obteniendo órdenes del cliente:", error.message);
      console.error("📍 Stack:", error.stack);
      throw error;
    }
  }
}

class GestionSucursales {
  static async procesoSucursales() {
    // Consulta GraphQL optimizada - solo name e id
    const consultaGrapql = `query GetAllLocations {
      locations(first: 10) {
        edges {
          node {
            id
            name
          }
        }
      }
    }`;

    try {
      const response = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: consultaGrapql }),
      });

      if (!response.ok) {
        throw new Error(`Error de red: ${response.status}`);
      }

      const { data, errors } = await response.json();

      if (errors) {
        console.error("❌ Errores GraphQL:", errors);
        return;
      }

      if (!data?.locations?.edges) {
        console.error("❌ Estructura de datos inesperada");
        return;
      }

      // Crear mapa para búsqueda O(1)
      const sucursalesGraphQL = new Map(
        data.locations.edges.map(({ node }) => [node.name, node.id])
      );

      let actualizadas = 0;
      let noEncontradas = 0;

      // Actualizar solo el ID de cada sucursal
      window.sucursales.forEach((sucursal) => {
        const idShopify = sucursalesGraphQL.get(sucursal.nombre);

        if (idShopify) {
          sucursal.id = idShopify;
          actualizadas++;
        } else {
          console.warn(`⚠️ "${sucursal.nombre}" no existe en Shopify`);
          noEncontradas++;
        }
      });

      // Guardar en localStorage
      localStorage.setItem("ph-sucursales", JSON.stringify(window.sucursales));

    } catch (error) {
      console.error("❌ Error en procesoSucursales:", error.message);
    }
  }

  static async obtenerUbicacion() {
    try {
      const posicion = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const coordenadas = {
        lat: posicion.coords.latitude,
        lng: posicion.coords.longitude,
      };

      localStorage.setItem(
        "ph-coordenadas-usuario",
        JSON.stringify(coordenadas)
      );
      return coordenadas;
    } catch {
      const coordenadasDefecto = {
        lat: -17.783315017953004,
        lng: -63.18214577296119,
      };

      localStorage.setItem(
        "ph-coordenadas-usuario",
        JSON.stringify(coordenadasDefecto)
      );
      return coordenadasDefecto;
    }
  }

  static validarHorarioApertura(turnos) {
    const ahora = new Date();

    // Obtener día de la semana (1=Lunes, 7=Domingo)
    const diaSemana = ahora.getDay() === 0 ? 7 : ahora.getDay();

    // Obtener hora actual en formato "HH:MM"
    const horaActual = ahora.toTimeString().slice(0, 5);

    // Buscar turno del día actual
    const turnoHoy = turnos.find(turno => turno.dias.includes(diaSemana));

    // Si no hay turno hoy = cerrado
    if (!turnoHoy) return false;

    // Convertir horas a minutos
    const minutosActual = this.horaAMinutos(horaActual);
    const minutosInicial = this.horaAMinutos(turnoHoy.horaInicial);
    let minutosFinal = this.horaAMinutos(turnoHoy.horaFinal);

    // Obtener la hora de cierre como número para verificar si cruza medianoche
    const [horaFin] = turnoHoy.horaFinal.split(':').map(Number);

    // Manejar cierre después de medianoche (ej: 00:00, 01:00)
    // Si la hora de cierre es menor que la de inicio, suma 24 horas
    if (minutosFinal <= minutosInicial && horaFin < 6) {
      minutosFinal += 24 * 60; // Agregar 24 horas en minutos
    }

    // Ajustar hora actual si estamos en madrugada y hay cruce de medianoche
    let minutosActualAjustado = minutosActual;
    if (minutosFinal > 1440 && minutosActual < 360) {
      minutosActualAjustado = minutosActual + 24 * 60;
    }

    // Verificar si está abierto
    if (minutosFinal > minutosInicial) {
      // Caso normal (mismo día) o cruce de medianoche
      return minutosActualAjustado >= minutosInicial && minutosActualAjustado < minutosFinal;
    }

    // Si no se cumple ninguna condición, está cerrado
    return false;
  }

  static horaAMinutos(hora) {
    const [horas, minutos] = hora.split(':').map(Number);
    return horas * 60 + minutos;
  }

  static corroborarInfoShopify(sucursalesEnShopify, sucursalSeleccionada) {

    // Validacion de variables de entrada
    if (!sucursalesEnShopify) {
      console.warn('Variables de entrada invalidas: sucursalesEnShopify es null');
      return null;
    }

    // Si no hay sucursal seleccionada, usar la primera por defecto
    if (!sucursalSeleccionada) {
      if (sucursalesEnShopify.length > 0) {
        console.log('No hay sucursal en localStorage, usando la primera sucursal por defecto');
        const sucursalPorDefecto = sucursalesEnShopify[3];
        window.sucursal = sucursalPorDefecto;
        return sucursalPorDefecto;
      } else {
        console.warn('No hay sucursales disponibles');
        return null;
      }
    }

    // Buscar sucursal por nombre
    const sucursalEnShopify = sucursalesEnShopify.find(sucursal =>
      sucursal.nombre === sucursalSeleccionada.nombre
    );

    if (!sucursalEnShopify) {
      console.warn(`Sucursal "${sucursalSeleccionada.nombre}" no encontrada en Shopify`);
      // Si no se encuentra, usar la primera sucursal por defecto
      if (sucursalesEnShopify.length > 0) {
        console.log('Usando la primera sucursal disponible por defecto');
        const sucursalPorDefecto = sucursalesEnShopify[3];
        window.sucursal = sucursalPorDefecto;
        return sucursalPorDefecto;
      }
      return null;
    }

    // Campos a comparar
    const campos = ['direccion', 'telefono', 'localizacion', 'backend'];
    let actualizado = false;

    // Comparar y actualizar campos
    campos.forEach(campo => {
      if (sucursalSeleccionada[campo] !== sucursalEnShopify[campo]) {
        sucursalSeleccionada[campo] = sucursalEnShopify[campo];
        actualizado = true;
      }
    });

    //  Actualizar window.sucursales con la sucursal actualizada
    if (actualizado) {
      // Buscar la sucursal en window.sucursales y actualizarla
      const indiceSucursal = window.sucursales.findIndex(s => s.nombre === sucursalSeleccionada.nombre);
      if (indiceSucursal !== -1) {
        window.sucursales[indiceSucursal] = sucursalSeleccionada;
      }
    }

    //  Devolver el objeto Sucursal actualizado
    window.sucursal = sucursalSeleccionada;
    return sucursalSeleccionada;
  }
}

class GestorOfServiciosPagos {
  static async iniciarPasarela(datosCheckout, thisContexto) {
    let interval = null;

    try {
      // Paso 1: Login y obtener token
      const loginResponse = await fetch(`${thisContexto.url}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: thisContexto.user,
          password: thisContexto.pass,
        }),
      });

      const loginData = await loginResponse.json();
      const token = loginData.access;

      // Paso 2: Preparar datos del QR
      const transactionId = `${thisContexto.informacionUsuario.id
        .split("/")
        .pop()}`;

      const now = new Date(); //
      now.setHours(now.getHours() - 4); // Restamos 4 horas
      const timestamp = now.toISOString(); // Formato ISO con desfase horario

      const qrResponse = await fetch(`${thisContexto.url}/qr/generate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          transaction: timestamp,
          amount:
            thisContexto.totalCarrito +
            (thisContexto.estadoPagina === "domicilio"
              ? window.costoEnvioDomicilio
              : 0),
          gloss: `Pago pedido Pizza Hut ${transactionId}`,
          pos: "POS 1",
          cashier: "PHWeb",
          phoneNumber: `+591${thisContexto.inputCelularContacto.value}`,
          customData: [
            `${thisContexto.inputNombreContacto.value} ${thisContexto.inputApellidoContacto.value}`,
            "1",
          ],
          lifespan: 300,
          branchOffice: "TMP",
          regional: "001",
        }),
      });

      const qrData = await qrResponse.json();
      const qrId = qrData.idPublic;

      // Paso 3: Mostrar QR en modal
      MensajeCargaDatos.ocultar();
      thisContexto.contenedorBaseModal.style.display = "flex";
      thisContexto.contenedorBaseMensaje.style.display = "flex";
      thisContexto.contenedorQR.style.display = "flex";

      thisContexto.contenedorQR.innerHTML = `
      <iframe id="ifarma" src="https://qr.farmacorp.com/viewer/${qrId}" alt="QR de pago" height="760px" width="100%"></iframe>
      <p>Escanea el QR para pagar</p>
      `;

      // Paso 4: Configurar intervalo para verificar periódicamente el estado del pago
      interval = setInterval(async () => {
        try {
          const paidResponse = await fetch(
            `${thisContexto.url}/qr/${qrId}/paid`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!paidResponse.ok) {
            const errorText = await paidResponse.text();
            console.error("Error en /paid:", paidResponse.status, errorText);
            return;
          }

          const wasPaid = await paidResponse.json(); // Booleano true o falsee
          console.log("¿Pago confirmado?", wasPaid);

          if (wasPaid === true) {
            clearInterval(interval);
            interval = null;
            thisContexto.contenedorQR.innerHTML = `
              <div class="ph-modal-body-exito">
                <img src="${window.assets.imagen_check_verde}" alt="Icono de exito" width="100%" height="100%">
                <small>PAGO CONFIRMADO.</small>
              </div>
            `;

            setTimeout(async () => {
              thisContexto.contenedorBaseModal.style.display = "none";
              thisContexto.contenedorBaseMensaje.style.display = "none";
              thisContexto.contenedorQR.style.display = "none";

              MensajeCargaDatos.mostrar(
                "Espere un momento, su pedido se está procesando."
              );
              thisContexto.idQr = qrId;
              const informacionPedido =
                await GestionOrdenesShopify.crearOrdenCompletada(
                  datosCheckout,
                  thisContexto
                );
              thisContexto.infoOrden = informacionPedido.order;
              thisContexto.nombreOrden = informacionPedido.order.name;

              const todalElementoCarrito =
                thisContexto.infoCarrito.informacionCompleta.items.length;
              const idFulFillmentOrder =
                await GestionOrdenesShopify.obtenerFulfillmentOrders(
                  thisContexto.infoOrden.id,
                  todalElementoCarrito
                );
              await GestionOrdenesShopify.moverFulfillmentOrder(
                idFulFillmentOrder,
                thisContexto.sucursalTrabajo.id
              );

              console.log("informacionPedido", informacionPedido);
              localStorage.setItem("ph-id-orden", informacionPedido.order.id);
              localStorage.setItem("ph-estadoDP", "etapa-1");
              await GestorOfServiciosPagos.actualizarQR(token, thisContexto);
              await AuxiliaresGlobal.limpiarCarrito();
              MensajeCargaDatos.ocultar();

              clearInterval(interval);
              interval = null;
              window.location.replace("/pages/detalle-pedido");
            }, 1500);
          }
        } catch (error) {
          console.error("Error al verificar estado de pago:", error);
          if (interval) {
            clearInterval(interval);
            interval = null;
            console.log("Intervalo limpiado debido a error");
          }
        }
      }, 10000); // Verifica cada 10 segundoss
    } catch (error) {
      if (interval) {
        clearInterval(interval);
        interval = null;
        console.log("Intervalo limpiado debido a error");
      }
      console.error("Error al procesar pago:", error);
      thisContexto.contenedorQR.innerHTML = `<p>⚠️ Error al generar el QR.</p>`;
    }
  }

  static async actualizarQR(token, thisContexto) {
    try {
      var codigoSucursal = "";
      if (thisContexto.estadoPagina == "domicilio") {
        const sucursalTrabajo = AuxiliaresGlobal.obtenerSucursalTrabajo(
          thisContexto.infoDominicilioSeleccionado);
        const numeroParte = sucursalTrabajo.backend.split("-")[1];
        codigoSucursal = `${sucursalTrabajo.backend.split("-")[0]
          }-${numeroParte.padStart(2, "0")}`;
      } else {
        const numeroParte =
          thisContexto.localSeleccionado.backend.split("-")[1];
        codigoSucursal = `${thisContexto.localSeleccionado.backend.split("-")[0]
          }-${numeroParte.padStart(2, "0")}`;
      }

      const response = await fetch(
        `${thisContexto.url}/qr/${thisContexto.idQr}/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            transaction: thisContexto.nombreOrden,
            Info: "actualizacion qr",
            BranchOffice: codigoSucursal,
          }),
        }
      );

      // Verificar primero si la respuesta se completó correctamente
      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      // Verificar el tipo de contenido de la respuesta
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const data = await response.json();
        console.log("Éxito:", data);
        return data;
      } else {
        // Si no es JSON, leer como texto
        const text = await response.text();
        console.log("Respuesta (no JSON):", text);
        return {
          success: true,
          message: "Operación completada",
          responseText: text,
        };
      }
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }
}

class GestorProductos {
  static calcularDescuento(precio, precioComparacion) {
    const descuento = ((precioComparacion - precio) / precioComparacion) * 100;
    return `-${descuento.toFixed(0)}%`;
  }

  // Estilo deacuerdo al dispositivo, del titulo del producto
  static estiloTituloProducto(titulo) {
    if (window.innerWidth >= 1024) {
      // Desktop (1024px+)
      return titulo.length > 30
        ? "line-clamp: 2; -webkit-line-clamp: 2;"
        : "line-clamp: 3; -webkit-line-clamp: 3;";
    } else if (window.innerWidth >= 768) {
      // Tablet (768px - 1023px)
      return titulo.length > 25
        ? "line-clamp: 2; -webkit-line-clamp: 2;"
        : "line-clamp: 3; -webkit-line-clamp: 3;";
    } else {
      // Mobile (menos de 768px)
      return titulo.length > 15
        ? "line-clamp: 2; -webkit-line-clamp: 2;"
        : "line-clamp: 3; -webkit-line-clamp: 3;";
    }
  }

  // Se le va pasar un array de colecciones (títulos) y va a traer los productos de esas colecciones
  static async obtenerProductosDeColecciones(titulosColeccion) {
    if (!Array.isArray(titulosColeccion) || titulosColeccion.length === 0) {
      console.warn("[obtenerProductosDeColecciones] Array vacío");
      return [];
    }

    // Construir consulta GraphQL SIN metafields de colecciones
    const condicionConsulta = titulosColeccion
      .map((titulo) => `title:\\"${titulo}\\"`)
      .join(" OR ");

    const consultaGraphQL = `
      query ObtenerProductosDeColecciones {
        collections(first: ${titulosColeccion.length}, query: "${condicionConsulta}") {
          edges {
            node {
              id
              title
              image { url }
              products(first: 50) {
                edges {
                  node {
                    id
                    handle
                    title
                    description
                    tags
                    totalInventory
                    collections(first: 10) {
                      edges {
                        node {
                          title
                        }
                      }
                    }
                    images(first: 1) { edges { node { url } } }
                    metafield(namespace: "estructura", key: "PersonalizableSecciones") {
                      value
                    }
                    variants(first: 1) {
                      edges {
                        node {
                          id
                          price
                          compareAtPrice
                          availableForSale
                          inventoryItem {
                            inventoryLevels(first: 5) {
                              edges {
                                node {
                                  location { name }
                                  quantities(names: ["available"]) { name quantity }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
    }`;

    try {
      const respuesta = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: consultaGraphQL }),
      });

      if (!respuesta.ok) throw new Error(`Error de red: ${respuesta.status}`);

      const datosRespuesta = await respuesta.json();

      if (datosRespuesta.errors || !datosRespuesta.data?.collections) {
        console.error("[obtenerProductosDeColecciones] Error en respuesta:", datosRespuesta.errors);
        return [];
      }

      // OPTIMIZACIÓN 1: Convertir array a Map para búsqueda O(1)
      const mapaCategoriasColeccion = new Map(
        (window.detalleColeccionesTrabajo || []).map(col => [col.titulo, col.categoria])
      );

      // OPTIMIZACIÓN 2: Usar Set para búsqueda O(1)
      const ramasProductoSet = new Set(["PCT", "PCE", "PCMB", "PPT", "PPE", "PPMB"]);

      // OPTIMIZACIÓN 3: Reutilizar imagen por defecto
      const imagenPorDefecto = window.assets?.imagen_aux || "";

      const resultado = [];
      const colecciones = datosRespuesta.data.collections.edges || [];

      for (const coleccionEdge of colecciones) {
        const datosColeccion = coleccionEdge?.node;
        if (!datosColeccion) continue;

        const categoriaColeccion = mapaCategoriasColeccion.get(datosColeccion.title) || "";
        const esRamaProducto = ramasProductoSet.has(datosColeccion.title);

        const productos = [];
        const productosEdges = datosColeccion.products?.edges || [];

        for (const productoEdge of productosEdges) {
          const producto = productoEdge?.node;
          if (!producto) continue;

          const primeraVariante = producto.variants?.edges?.[0]?.node;
          const idVariante = primeraVariante?.id?.split("/").pop() || "";

          // OPTIMIZACIÓN 4: Procesar sucursales de forma más eficiente
          const sucursales = [];
          const nivelesInventario = primeraVariante?.inventoryItem?.inventoryLevels?.edges || [];

          for (const nivelEdge of nivelesInventario) {
            const nivel = nivelEdge?.node;
            if (!nivel) continue;

            const cantidadDisponible = nivel.quantities?.find(q => q.name === "available");
            sucursales.push({
              nombre: nivel.location.name,
              stock: cantidadDisponible?.quantity || 0,
            });
          }

          // OPTIMIZACIÓN 5: Parse JSON solo una vez y reutilizar
          const metafieldValue = producto.metafield?.value || "{}";
          const datosMetafield = metafieldValue !== "{}" ? JSON.parse(metafieldValue) : {};

          // OPTIMIZACIÓN 6: Evitar operadores ternarios redundantes
          const imagenProducto = producto.images?.edges?.[0]?.node?.url || imagenPorDefecto;

          // OPTIMIZACIÓN 7: Crear producto sin try-catch (más rápido)
          const productoObjeto = new Producto({
            idshopify: idVariante,
            idtrabajo: datosMetafield.id || producto.handle,
            handle: producto.handle || "",
            nombre: producto.title || "",
            descripcion: producto.description || "",
            tags: producto.tags || [],
            colecciones: (producto.collections?.edges || []).map(edge => edge.node.title),
            estado: primeraVariante?.availableForSale || false,
            precio: parseFloat(primeraVariante?.price || 0),
            precio_comparacion: parseFloat(primeraVariante?.compareAtPrice || 0),
            obj_num: datosMetafield.object_number || "",
            imagen: imagenProducto,
            stockTotal: producto.totalInventory || 0,
            sucursales: sucursales,
            metafield: datosMetafield,
            tipoProducto: categoriaColeccion,
            dia: datosMetafield.dia || "",
            url: `/products/${producto.handle}`,
          });

          productos.push(productoObjeto);
        }

        // OPTIMIZACIÓN 8: Crear colección sin try-catch y sin metafields
        const coleccionObjeto = new Coleccion({
          id: datosColeccion.id,
          nombre: datosColeccion.title,
          imagen: datosColeccion.image?.url || "",
          productos: productos,
          metafields: [],
          tipo: categoriaColeccion,
        });

        resultado.push(coleccionObjeto);
      }

      return resultado;
    } catch (error) {
      console.error("[obtenerProductosDeColecciones] Error:", error);
      return [];
    }
  }

  // Array[] titulos de colecciones y devolver solamente info de productos de cada coleccion
  static async getProductosDeColeccionesEspecificas(titulosColeccion) {
    if (!Array.isArray(titulosColeccion) || titulosColeccion.length === 0) {
      console.warn(
        "[getProductosDeColeccionesEspecificas] El parámetro titulosColeccion debe ser un array no vacío"
      );
      return [];
    }

    // Construir la condición de búsqueda para GraphQL
    const condicionConsulta = titulosColeccion
      .map((titulo) => `title:\\"${titulo}\\"`)
      .join(" OR ");

    const consultaGraphQL = `
      query ObtenerProductosDeColeccionesEspecificas {
        collections(first: ${titulosColeccion.length}, query: "${condicionConsulta}") {
          edges {
            node {
              title
              products(first: 50) {
                edges {
                  node {
                    id
                    handle
                    title
                    description
                    totalInventory
                    tags
                    collections(first: 5) { 
                      edges { 
                        node { 
                          title 
                        } 
                      } 
                    }
                    images(first: 1) { 
                      edges { 
                        node { 
                          url 
                        } 
                      } 
                    }
                    metafield(namespace: "estructura", key: "PersonalizableSecciones") {
                      value
                    }
                    variants(first: 1) {
                      edges {
                        node {
                          id
                          title
                          price
                          compareAtPrice
                          availableForSale
                          inventoryItem {
                            inventoryLevels(first: 5) {
                              edges {
                                node {
                                  location { name id }
                                  quantities(names: ["available"]) { name quantity }
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const respuesta = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: consultaGraphQL }),
      });

      if (!respuesta.ok) {
        console.error(
          `[obtenerProductosDeColecciones] Error de red: ${respuesta.status} ${respuesta.statusText}`
        );
        throw new Error(`Error de red: ${respuesta.status}`);
      }

      let datosRespuesta;
      try {
        datosRespuesta = await respuesta.json();
      } catch (error) {
        console.error(
          "[obtenerProductosDeColecciones] Error al parsear JSON de la respuesta:",
          error
        );
        throw new Error("La respuesta de Shopify no es un JSON válido");
      }

      if (datosRespuesta.errors) {
        console.error(
          "[obtenerProductosDeColecciones] Errores en la consulta GraphQL:",
          datosRespuesta.errors
        );
        throw new Error("Error en la consulta GraphQL");
      }

      if (!datosRespuesta.data || !datosRespuesta.data.collections) {
        console.error(
          "[obtenerProductosDeColecciones] Respuesta sin data.collections:",
          datosRespuesta
        );
        throw new Error("Respuesta de Shopify sin data.collections");
      }

      // Procesar los resultados
      const resultado = [];
      const colecciones = datosRespuesta.data.collections.edges || [];

      // Cachear el detalle de colecciones para evitar búsquedas repetidas
      const detalleColecciones = window.detalleColeccionesTrabajo || [];

      colecciones.forEach((coleccionEdge, indiceColeccion) => {
        if (!coleccionEdge || !coleccionEdge.node) {
          console.warn(
            `[obtenerProductosDeColecciones] Colección vacía en el índice ${indiceColeccion}`
          );
          return;
        }

        const datosColeccion = coleccionEdge.node;
        const categoriaColeccion =
          detalleColecciones.find(
            (coleccion) => coleccion.titulo === datosColeccion.title
          )?.categoria || "";
        const esRamaProducto = [
          "PCT",
          "PCE",
          "PCMB",
          "PPT",
          "PPE",
          "PPMB",
        ].includes(datosColeccion.title);

        // Procesar productos
        const productosDeColeccion = (datosColeccion.products?.edges || [])
          .map((productoEdge, indiceProducto) => {
            if (!productoEdge || !productoEdge.node) {
              console.warn(
                `[obtenerProductosDeColecciones] Producto vacío en colección "${datosColeccion.title}" índice ${indiceProducto}`
              );
              return null;
            }
            const producto = productoEdge.node;
            const varianteNodo = producto.variants?.edges?.[0]?.node;
            const idVariante = varianteNodo?.id?.split("/").pop() || "";

            // Procesar sucursales para todos los productos
            const sucursales = [];
            const nivelesInventario =
              varianteNodo?.inventoryItem?.inventoryLevels?.edges || [];
            for (const nivelEdge of nivelesInventario) {
              if (!nivelEdge || !nivelEdge.node) continue;
              const nivel = nivelEdge.node;
              const cantidadDisponible = nivel.quantities?.find(
                (q) => q.name === "available"
              );
              sucursales.push({
                nombre: nivel.location.name,
                stock: cantidadDisponible?.quantity || 0,
              });
            }

            // Parsear metafield de forma segura
            let datosMetafield = {};
            if (producto.metafield?.value) {
              try {
                datosMetafield = JSON.parse(producto.metafield.value);
              } catch (error) {
                console.warn(
                  `[obtenerProductosDeColecciones] Error al parsear metafield en producto "${producto.title}":`,
                  error
                );
                datosMetafield = {};
              }
            }

            // Procesar colecciones - solo títulos en array simple
            const colecciones = (producto.collections?.edges || []).map(
              (edge) => edge.node.title
            );

            // Procesar tags
            const tags = producto.tags || [];

            try {
              return new Producto({
                idshopify: idVariante,
                idtrabajo: datosMetafield.id || producto.handle,
                handle: producto.handle,
                nombre: producto.title,
                descripcion: producto.description,
                estado: varianteNodo?.availableForSale || false,
                precio: varianteNodo?.price || 0,
                precio_comparacion: varianteNodo?.compareAtPrice || 0,
                obj_num: datosMetafield.object_number || "",
                imagen:
                  producto.images?.edges?.length > 0
                    ? producto.images.edges[0].node.url
                    : window.assets.imagen_aux,
                stockTotal: producto.totalInventory || 0,
                sucursales: sucursales,
                metafield: datosMetafield,
                tipoProducto: categoriaColeccion,
                dia: datosMetafield.dia || "",
                colecciones: colecciones,
                tags: tags,
              });
            } catch (error) {
              console.error(
                `[obtenerProductosDeColecciones] Error al crear el objeto Producto para "${producto.title}":`,
                error
              );
              return null;
            }
          })
          .filter(Boolean);

        try {
          resultado.push(
            new Coleccion({
              id: "",
              nombre: datosColeccion.title,
              imagen: "",
              productos: productosDeColeccion,
              metafields: datosColeccion.metafields?.edges?.map(
                (metaEdge) => metaEdge.node
              ),
              tipo: categoriaColeccion,
            })
          );
        } catch (error) {
          console.error(
            `[obtenerProductosDeColecciones] Error al crear el objeto Coleccion para "${datosColeccion.title}":`,
            error
          );
        }
      });

      return resultado;
    } catch (error) {
      console.error("[obtenerProductosDeColecciones] Error general:", error);
      return [];
    }
  }

  // Se le va pasar el titulo de un producto y se va traer toda la informacion
  static async getProductoPorTitulo(titulo) {
    // Validar parámetros de entrada
    if (!titulo) {
      console.warn(
        "[getProductoPorTitulo] El título/handle del producto es requerido"
      );
      return null;
    }

    // Determinar si es handle (solo números) o título (contiene letras/caracteres)
    const esSoloNumeros = /^\d+$/.test(titulo);
    const tipoBusqueda = esSoloNumeros ? "handle" : "title";
    const queryBusqueda = esSoloNumeros
      ? `handle:\\"${titulo}\\"`
      : `title:\\"${titulo}\\"`;

    console.log(`🔍 Buscando producto por ${tipoBusqueda}:`, titulo);

    // Construir consulta GraphQL
    const consultaGraphQL = `
      query GetProductoPorTitulo {
        products(first: 1, query: "${queryBusqueda}") {
          edges {
            node {
              id
              handle
              title
              description
              totalInventory
              tags
              collections(first: 8) {
                edges {
                  node {
                    title
                    handle
                  }
                }
              }
              images(first: 1) {
                edges {
                  node {
                    url
                  }
                }
              }
              variants(first: 1) {
                edges {
                  node {
                    id
                    title
                    price
                    compareAtPrice
                    availableForSale
                    inventoryItem {
                      inventoryLevels(first: 5) {
                        edges {
                          node {
                            location {
                              name
                              id
                            }
                            quantities(names: ["available"]) {
                              name
                              quantity
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              metafield(namespace: "estructura", key: "PersonalizableSecciones") {
                value
              }
            }
          }
        }
      }
    `;

    try {
      // Realizar petición a Shopify
      const respuesta = await fetch(window.urlConsulta, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: consultaGraphQL }),
      });

      if (!respuesta.ok) {
        console.error(
          `[getProductoPorTitulo] Error de red: ${respuesta.status} ${respuesta.statusText}`
        );
        throw new Error(`Error de red: ${respuesta.status}`);
      }

      // Parsear respuesta JSON
      let datosRespuesta;
      try {
        datosRespuesta = await respuesta.json();
      } catch (error) {
        console.error(
          "[getProductoPorTitulo] Error al parsear JSON de la respuesta:",
          error
        );
        throw new Error("La respuesta de Shopify no es un JSON válido");
      }

      // Validar respuesta
      if (datosRespuesta.errors) {
        console.error(
          "[getProductoPorTitulo] Errores en la consulta GraphQL:",
          datosRespuesta.errors
        );
        throw new Error("Error en la consulta GraphQL");
      }

      if (!datosRespuesta.data?.products?.edges?.[0]?.node) {
        console.error("[getProductoPorTitulo] Producto no encontrado:", titulo);
        return null;
      }

      // Procesar producto
      const producto = datosRespuesta.data.products.edges[0].node;
      const idVariante =
        producto.variants?.edges?.[0]?.node?.id?.split("/").pop() || "";
      const metafields = (producto.metafields?.edges || []).map(
        (metaEdge) => metaEdge.node
      );

      // Procesar sucursales
      const sucursales = [];
      const nivelesInventario =
        producto.variants?.edges?.[0]?.node?.inventoryItem?.inventoryLevels
          ?.edges || [];

      for (const nivelEdge of nivelesInventario) {
        if (!nivelEdge?.node) continue;

        const nivel = nivelEdge.node;
        const cantidadDisponible = nivel.quantities?.find(
          (q) => q.name === "available"
        );

        sucursales.push({
          nombre: nivel.location.name,
          stock: cantidadDisponible?.quantity || 0,
        });
      }

      // Procesar colecciones
      const colecciones = (producto.collections?.edges || []).map(
        (edge) => edge.node.title
      );

      const detalleColecciones = window.detalleColeccionesTrabajo || [];
      const categoriaColeccion =
        detalleColecciones.find(
          (coleccion) =>
            coleccion.titulo === producto.collections?.edges?.[0]?.node?.title
        )?.categoria || "";

      // Procesar tags
      const tags = producto.tags || [];

      // Parsear metafields
      let datosMetafield = {};
      if (producto.metafield?.value) {
        try {
          datosMetafield = JSON.parse(producto.metafield.value);
        } catch (error) {
          console.warn(
            `[getProductoPorTitulo] Error al parsear metafield en producto "${producto.title}":`,
            error
          );
          datosMetafield = {};
        }
      }

      // Crear objeto Producto
      try {
        return new Producto({
          idshopify: idVariante,
          idtrabajo: datosMetafield.id || producto.handle,
          obj_num: datosMetafield.object_number || "",
          handle: producto.handle,
          nombre: producto.title,
          descripcion: producto.description,
          estado: producto.variants?.edges?.[0]?.node?.availableForSale || false,
          precio: producto.variants?.edges?.[0]?.node?.price || 0,
          precio_comparacion: producto.variants?.edges?.[0]?.node?.compareAtPrice || 0,
          imagen:
            producto.images?.edges?.[0]?.node?.url || window.assets.imagen_aux,
          stockTotal: producto.totalInventory || 0,
          sucursales: sucursales,
          metafield: datosMetafield,
          tipoProducto: categoriaColeccion,
          dia: datosMetafield.dia || "",
          colecciones: colecciones,
          tags: tags,
        });
      } catch (error) {
        console.error(
          `[getProductoPorTitulo] Error al crear Producto para "${producto.title}":`,
          error
        );
        return null;
      }
    } catch (error) {
      console.error("[getProductoPorTitulo] Error general:", error);
      return null;
    }
  }

  // ===== VALIDACION PRODUCTO (PROMOCION) -> (DIA Y HORA) =====
  static activacionProductoByDate(parametro) {
    // Si el parámetro es null, undefined o vacío, retornar siempre habilitado
    if (parametro == null || parametro == undefined || parametro == "") {
      return {
        activado: true,
        mensaje: "",
      };
    }

    // Días de la semana en español
    const diasSemana = [
      "Domingo",
      "Lunes",
      "Martes",
      "Miercoles",
      "Jueves",
      "Viernes",
      "Sabado",
    ];

    // Obtener fecha y hora actual del dispositivo
    const fechaActual = new Date();
    const diaHoyTexto = diasSemana[fechaActual.getDay()];
    const horaActual24 = fechaActual.getHours(); // 0-23
    const minutosActuales = fechaActual.getMinutes(); // 0-59
    const minutosActualesTotal = horaActual24 * 60 + minutosActuales; // Total en minutos desde medianoche

    // Variables para controlar si pasa las validaciones
    let diaValido = true;
    let horaValida = true;

    // Variables para generar el mensaje de error
    let mensajeDias = "";
    let mensajeHoras = "";

    // Separar el parámetro por '+' para obtener días y horas por separado
    // Ejemplo: "Lunes - Miercoles + 8am - 10pm" se convierte en ["Lunes - Miercoles", "8am - 10pm"]
    const partesParametro = parametro.split("+");

    // Procesar cada parte del parámetro
    for (let i = 0; i < partesParametro.length; i++) {
      const parteActual = partesParametro[i].trim(); // Quitar espacios al inicio y final

      // Verificar si esta parte contiene horas (am o pm)
      if (parteActual.includes("am") || parteActual.includes("pm")) {
        // ===== VALIDACIÓN DE HORAS =====
        horaValida = false; // Asumir que no es válida hasta comprobar

        // Guardar el texto original para el mensaje
        mensajeHoras = parteActual;

        // Separar el rango de horas: "8am - 10pm" -> ["8am", "10pm"]
        const rangoHoras = parteActual.split("-");
        if (rangoHoras.length === 2) {
          const horaInicioTexto = rangoHoras[0].trim();
          const horaFinTexto = rangoHoras[1].trim();

          // Convertir hora de inicio a minutos totales
          let minutosInicio = -1;
          const horaInicioLimpia = horaInicioTexto
            .replace(/\s+/g, "")
            .toLowerCase();
          const esAMInicio = horaInicioLimpia.includes("am");
          const esPMInicio = horaInicioLimpia.includes("pm");

          if (esAMInicio || esPMInicio) {
            const numeroInicioTexto = horaInicioLimpia.replace(/am|pm/g, "");
            let horasInicio, minutosInicioHora;

            if (numeroInicioTexto.includes(":")) {
              // Formato con minutos: "8:30"
              const partesInicio = numeroInicioTexto.split(":");
              horasInicio = parseInt(partesInicio[0]);
              minutosInicioHora = parseInt(partesInicio[1]);
            } else {
              // Solo horas: "8"
              horasInicio = parseInt(numeroInicioTexto);
              minutosInicioHora = 0;
            }

            // Convertir a formato 24 horas
            if (esPMInicio && horasInicio !== 12) {
              horasInicio += 12;
            } else if (esAMInicio && horasInicio === 12) {
              horasInicio = 0;
            }

            // Validar que los números sean correctos
            if (
              !isNaN(horasInicio) &&
              !isNaN(minutosInicioHora) &&
              horasInicio >= 0 &&
              horasInicio <= 23 &&
              minutosInicioHora >= 0 &&
              minutosInicioHora <= 59
            ) {
              minutosInicio = horasInicio * 60 + minutosInicioHora;
            }
          }

          // Convertir hora de fin a minutos totales
          let minutosFin = -1;
          const horaFinLimpia = horaFinTexto.replace(/\s+/g, "").toLowerCase();
          const esAMFin = horaFinLimpia.includes("am");
          const esPMFin = horaFinLimpia.includes("pm");

          if (esAMFin || esPMFin) {
            const numeroFinTexto = horaFinLimpia.replace(/am|pm/g, "");
            let horasFin, minutosFinHora;

            if (numeroFinTexto.includes(":")) {
              // Formato con minutos: "10:45"
              const partesFin = numeroFinTexto.split(":");
              horasFin = parseInt(partesFin[0]);
              minutosFinHora = parseInt(partesFin[1]);
            } else {
              // Solo horas: "10"
              horasFin = parseInt(numeroFinTexto);
              minutosFinHora = 0;
            }

            // Convertir a formato 24 horas
            if (esPMFin && horasFin !== 12) {
              horasFin += 12;
            } else if (esAMFin && horasFin === 12) {
              horasFin = 0;
            }

            // Validar que los números sean correctos
            if (
              !isNaN(horasFin) &&
              !isNaN(minutosFinHora) &&
              horasFin >= 0 &&
              horasFin <= 23 &&
              minutosFinHora >= 0 &&
              minutosFinHora <= 59
            ) {
              minutosFin = horasFin * 60 + minutosFinHora;
            }
          }

          // Verificar si la hora actual está dentro del rango
          if (minutosInicio !== -1 && minutosFin !== -1) {
            if (minutosInicio <= minutosFin) {
              // Rango normal: 8am - 10pm
              horaValida =
                minutosActualesTotal >= minutosInicio &&
                minutosActualesTotal <= minutosFin;
            } else {
              // Rango que cruza medianoche: 10pm - 2am
              horaValida =
                minutosActualesTotal >= minutosInicio ||
                minutosActualesTotal <= minutosFin;
            }
          }
        }
      } else {
        // ===== VALIDACIÓN DE DÍAS =====
        diaValido = false; // Asumir que no es válido hasta comprobar

        // Guardar el texto original para el mensaje
        mensajeDias = parteActual;

        if (parteActual.includes("-")) {
          // Rango de días: "Lunes - Miercoles"
          const rangoDias = parteActual.split("-");
          if (rangoDias.length === 2) {
            const diaInicioTexto = rangoDias[0].trim();
            const diaFinTexto = rangoDias[1].trim();

            // Buscar los índices de los días en el array
            let indiceDiaInicio = -1;
            let indiceDiaFin = -1;
            let indiceDiaHoy = -1;

            for (let j = 0; j < diasSemana.length; j++) {
              if (diasSemana[j] === diaInicioTexto) indiceDiaInicio = j;
              if (diasSemana[j] === diaFinTexto) indiceDiaFin = j;
              if (diasSemana[j] === diaHoyTexto) indiceDiaHoy = j;
            }

            // Verificar si el día actual está en el rango
            if (
              indiceDiaInicio !== -1 &&
              indiceDiaFin !== -1 &&
              indiceDiaHoy !== -1
            ) {
              if (indiceDiaInicio <= indiceDiaFin) {
                // Rango normal: Lunes (1) - Miercoles (3)
                diaValido =
                  indiceDiaHoy >= indiceDiaInicio &&
                  indiceDiaHoy <= indiceDiaFin;
              } else {
                // Rango que cruza la semana: Viernes (5) - Lunes (1)
                diaValido =
                  indiceDiaHoy >= indiceDiaInicio ||
                  indiceDiaHoy <= indiceDiaFin;
              }
            }
          }
        } else {
          // Día específico: "Lunes"
          diaValido = parteActual === diaHoyTexto;
        }
      }
    }

    // Verificar si está activado (día Y hora válidos)
    const estaActivado = diaValido && horaValida;

    // Generar mensaje de error si no está activado
    let mensajeFinal = "";
    if (!estaActivado) {
      // Construir mensaje bonito
      if (mensajeDias && mensajeHoras) {
        // Tiene restricción de días y horas
        const diasFormateados = GestorProductos.formatearTextoDias(mensajeDias);
        const horasFormateadas =
          GestorProductos.formatearTextoHoras(mensajeHoras);
        mensajeFinal = `Disponible solamente ${diasFormateados} de ${horasFormateadas}`;
      } else if (mensajeDias) {
        // Solo tiene restricción de días
        const diasFormateados = GestorProductos.formatearTextoDias(mensajeDias);
        mensajeFinal = `Disponible solamente ${diasFormateados}`;
      } else if (mensajeHoras) {
        // Solo tiene restricción de horas
        const horasFormateadas =
          GestorProductos.formatearTextoHoras(mensajeHoras);
        mensajeFinal = `Disponible solamente de ${horasFormateadas}`;
      } else {
        // Error genérico
        mensajeFinal = "No disponible en este momento";
      }
    }

    return {
      activado: estaActivado,
      mensaje: mensajeFinal,
    };
  }

  static formatearTextoDias(textoDias) {
    if (textoDias.includes("-")) {
      // Rango de días: "Lunes - Miercoles" -> "de Lunes a Miércoles"
      const partes = textoDias.split("-");
      const diaInicio = partes[0].trim();
      const diaFin = partes[1].trim();
      return `de ${diaInicio} a ${diaFin}`;
    } else {
      // Día específico: "Lunes" -> "los Lunes"
      return `los ${textoDias}`;
    }
  }

  static formatearTextoHoras(textoHoras) {
    if (textoHoras.includes("-")) {
      // Rango de horas: "8am - 5pm" -> "8:00 AM a 5:00 PM"
      const partes = textoHoras.split("-");
      const horaInicio = partes[0].trim().toUpperCase();
      const horaFin = partes[1].trim().toUpperCase();

      // Formatear las horas para que se vean más bonitas
      const horaInicioFormateada =
        GestorProductos.formatearHoraBonita(horaInicio);
      const horaFinFormateada = GestorProductos.formatearHoraBonita(horaFin);

      return `${horaInicioFormateada} a ${horaFinFormateada}`;
    }
    return textoHoras.toUpperCase();
  }

  static formatearHoraBonita(hora) {
    // Convertir "8am" a "8:00 AM" o "8:30pm" a "8:30 PM"
    const horaLimpia = hora.replace(/\s+/g, "").toLowerCase();

    if (horaLimpia.includes(":")) {
      // Ya tiene minutos: "8:30am" -> "8:30 AM"
      return horaLimpia.replace("am", " AM").replace("pm", " PM");
    } else {
      // Solo horas: "8am" -> "8:00 AM"
      return horaLimpia.replace("am", ":00 AM").replace("pm", ":00 PM");
    }
  }

  static acortarTitulo(titulo) {
    // Priorizar tamaños primero (más específicos)
    const tamanos = ["Grande", "Familiar", "Personal"];
    const tipos = ["Clasica", "Premium"];

    // Buscar primero tamaños
    for (const tamano of tamanos) {
      if (titulo.includes(tamano)) {
        return tamano;
      }
    }

    // Si no hay tamaño, buscar tipo
    for (const tipo of tipos) {
      if (titulo.includes(tipo)) {
        return tipo;
      }
    }

    return titulo;
  }
  // ===== CARGA DE DATOS DE PRODUCTOS =====
  static async cargaYevaluacionProductos(coleccion) {

    // PASO 1 : OBTENER INFORMACION
    var productosRespuesta = await GestorProductos.obtenerProductosDeColecciones([coleccion]);

    // Validar que haya respuesta
    if (!productosRespuesta || productosRespuesta.length === 0) {
      console.warn('No se encontraron productos en la colección:', coleccion);
      return [];
    }

    var productos = productosRespuesta[0]?.productos || [];

    if (productos.length === 0) {
      console.warn('La colección no tiene productos:', coleccion);
      return [];
    }

    // PASO 2 : Validar si hay sucursal seleccionada
    if (window.sucursal && window.sucursal.nombre) {
      // Buscar la sucursal -> windows.sucursales (info Shopify) para evaluar estado
      const sucursaEnShopify = window.sucursales?.find(s => s.nombre == window.sucursal.nombre);

      productos = productos.filter((producto) => {
        // Validar estado de sucursal
        if (!sucursaEnShopify?.estado) return false;

        // Validar que el producto tenga sucursales
        if (!producto.sucursales || !Array.isArray(producto.sucursales)) {
          console.warn('Producto sin información de sucursales:', producto);
          return false;
        }

        // Buscar la sucursal en el producto
        const sucursalProducto = producto.sucursales.find(s => s.nombre == window.sucursal.nombre);

        // Validar que exista y tenga stock
        if (!sucursalProducto) {
          return false;
        }

        const stockDisponible = sucursalProducto.stock || 0;
        return stockDisponible > 0;
      });
    }

    return productos;
  }

  // ===== BÚSQUEDA DE PRODUCTOS PARA HEADER =====
  static async buscarProductosShopify(termino, signal = null) {
    try {
      // PASO 1: Validar término
      if (!termino || termino.trim().length === 0) {
        return [];
      }

      // PASO 2: Construir URL con ruta relativa (evita CORS)
      // Usar la API de predictive-search en lugar de suggest
      const url = `/search/suggest.json?q=${encodeURIComponent(termino)}&resources[type]=product&resources[limit]=8&section_id=predictive-search`;

      // PASO 3: Realizar petición con AbortController para cancelar si es necesario
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: signal // Permite cancelar la petición
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      // PASO 4: Parsear respuesta
      const data = await response.json();

      // PASO 5: Extraer productos
      const productos = data.resources?.results?.products || [];

      if (productos.length === 0) {
        console.log('⚠️ No hay productos para:', termino);
        return [];
      }

      // PASO 5.5: Filtrar productos prohibidos
      const coleccionesProhibidas = ['PCT', 'PCMB', 'PCE', 'PPT', 'PPMB', 'PPE'];
      const frasesProhibidas = [
        'Extra',
        'Tradicional',
        'Pan Pizza',
        'Crispy',
        'delgada y crujiente',
        'Personal para 1 persona',
        'Familiar para 4 a 5 personas',
        'Grande para 3 a 4 personas',
      ];

      const productosFiltrados = productos.filter(producto => {
        // Verificar si el producto tiene colecciones prohibidas
        const tieneColeccionProhibida = producto.tags?.some(tag =>
          coleccionesProhibidas.includes(tag)
        );

        // Verificar si el nombre contiene frases prohibidas
        const nombreProducto = producto.title?.toLowerCase() || '';
        const tieneFraseProhibida = frasesProhibidas.some(frase =>
          nombreProducto.includes(frase.toLowerCase())
        );

        // Retornar true solo si NO tiene colecciones ni frases prohibidas
        return !tieneColeccionProhibida && !tieneFraseProhibida;
      });

      if (productosFiltrados.length === 0) {
        console.log('⚠️ Todos los productos fueron filtrados para:', termino);
        return [];
      }

      // PASO 6: Mapear a instancias de Producto
      const resultados = productosFiltrados.map(producto => {
        return new Producto({
          idshopify: String(producto.id),
          handle: producto.handle,
          nombre: producto.title,
          descripcion: producto.body,
          precio: parseFloat(producto.price || producto.price_min),
          precio_comparacion: parseFloat(producto.compare_at_price_max),
          imagen: producto.image || producto.featured_image?.url || window.assets?.imagen_aux,
          colecciones: producto.tags,
          estado: producto.available,
          url: producto.url
        });
      });

      console.log('✅ Productos encontrados:', resultados.length);
      return resultados;

    } catch (error) {
      // Si es un error de cancelación, no mostrar error
      if (error.name === 'AbortError') {
        console.log('⏹️ Búsqueda cancelada');
        return [];
      }
      console.error('❌ Error en búsqueda:', error.message);
      return [];
    }
  }

  // ===== EVALUAR DISPONIBILIDAD DE UN PRODUCTO =====
  static async evaluarDisponibilidadProducto(item) {
    const producto = await GestorProductos.getProductoPorTitulo(item);
    var metafield = producto.metafield;
    const estadoPromo = GestorProductos.activacionProductoByDate(metafield.dia);
    if (estadoPromo.activado) {
      if (window.sucursal != null && window.sucursal != undefined && Object.keys(window.sucursal).length > 0) {
        console.log("Testing window.sucursal :", window.sucursal);
        const stockSucursal = producto.sucursales.find(s => s.nombre == window.sucursal.nombre).stock;
        if (stockSucursal > 0) {
          // Redirigir a la página del producto
          window.location.href = `/products/${producto.handle}`;
          return true;
        } else {
          MensajeTemporal.mostrarMensaje("Producto no disponible en esta sucursal. Llámanos al " + window.telefono_contact_center, "info", 3500);
          return false;
        }
      } else {
        window.location.href = `/products/${producto.handle}`;
        return;
      }
    } else {
      MensajeTemporal.mostrarMensaje(
        estadoPromo.mensaje == ""
          ? "Producto no disponible en este momento"
          : estadoPromo.mensaje
        , "info", 3500);
      return false;
    }
  }
}

class Sucursales {
  constructor(
    id,
    lat,
    lng,
    nombre,
    servicios,
    dias,
    horario,
    localizacion,
    telefono,
    backend = null
  ) {
    this.id = id;
    this.lat = lat;
    this.lng = lng;
    this.nombre = nombre;
    this.servicios = servicios;
    this.dias = dias;
    this.horario = horario;
    this.localizacion = localizacion;
    this.telefono = telefono;
    this.backend = backend;
  }

  // Método para obtener el número de sucursal sin ceros a la izquierda
  obtenerNumeroSucursal() {
    if (!this.backend) return null;
    const numero = this.backend.match(/\d+/)?.[0];
    return numero ? parseInt(numero).toString() : null;
  }

  // Método para verificar si ofrece un servicio específico
  ofreceServicio(servicio) {
    return this.servicios.includes(servicio);
  }

  // Método para calcular distancia desde un punto
  calcularDistancia(latOrigen, lngOrigen) {
    const R = 6371; // Radio de la Tierra en km
    const dLat = this.lat - latOrigen;
    const dLng = this.lng - lngOrigen;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(latOrigen) *
      Math.cos(this.lat) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Método para crear instancia desde objeto
  static desdeObjeto(datos) {
    return new Sucursales(
      datos.id,
      datos.lat,
      datos.lng,
      datos.name,
      datos.servicios,
      datos.dias,
      datos.horario,
      datos.localizacion,
      datos.telefono,
      datos.backend
    );
  }

  // Método para convertir a objeto plano
  aObjeto() {
    return {
      id: this.id,
      lat: this.lat,
      lng: this.lng,
      nombre: this.nombre,
      servicios: this.servicios,
      dias: this.dias,
      horario: this.horario,
      localizacion: this.localizacion,
      telefono: this.telefono,
      backend: this.backend,
    };
  }
}
